import {
  finite, clamp, interiorBounds, boundsFromCenterSize, overflowDistance,
  overlapRatio, orientedSize, componentLocalSize, MOTHERBOARD_PCB_THICKNESS, MOTHERBOARD_SOCKET_DECK_HEIGHT,
  transformLocalPoint, transformPointByClass, composeOrientationClasses,
  canonicalEnvelopeToWorld, canonicalPlacementToWorld
} from './geometry.js';
import { socketLocalForMotherboardStandard, pcieLocalForMotherboardStandard } from './motherboard-standard.js?v=20260817-pcieanchor10';
import { motherboardOrientationClass, motherboardOrientationForPcieEdge, motherboardOrientationSemantics } from './motherboard-orientation.js?v=20260817-relbridge9';
import { enumerateTopologyHypotheses, largestSupportedMotherboard, largestSupportedPsu } from './topology.js?v=20260817-relbridge9';
import { buildCaseCapabilityProfile } from './capability-profile.js';
import { buildConfigurationBranches, branchEffectiveState } from './configuration-branches.js?v=20260817-relbridge9';
import {
  PSU_HORIZONTAL_ORIENTATIONS, PSU_HORIZONTAL_FLIPPED_ORIENTATIONS, PSU_FRONT_FACING_ORIENTATIONS, PSU_NARROW_X_ORIENTATIONS, PSU_UPRIGHT_ORIENTATIONS,
  radiatorRepresentative, structuralCapabilityEnvelope, coolingAssemblyReservations, coolingAssemblyDepth,
  preferredRadiatorMounts, catalogRadiatorMountPreferences, modeUsesVerticalGpu
} from './structural-reservations.js?v=20260817-relbridge9';

const BOARD_NATIVE = Object.freeze({
  ITX:  { l:170, w:170, h:44 },
  DTX:  { l:203, w:170, h:44 },
  MATX: { l:244, w:244, h:44 },
  ATX:  { l:305, w:244, h:44 }
});
const PSU_NATIVE = Object.freeze({
  FLEX:  { l:150, w:40.5, h:81.5 },
  SFX:   { l:125, w:63.5, h:100 },
  'SFX-L': { l:125, w:63.5, h:125 },
  ATX:   { l:150, w:86, h:140 }
});

function isUprightSandwich(effective) {
  // Physical packaging frame only. A mode named "Vertical Layout" (e.g. VGPU/spine) may
  // resolve to sandwich while the chassis itself remains standard orientation.
  return String(effective?.layoutFamily || '').toLowerCase()==='sandwich'
    && (effective?.modeTraits?.uprightSandwich === true || effective?.physicalLayoutId === 'vertical_sandwich');
}

function cloneDims(dims, fallback) {
  return {
    l:finite(dims?.l, fallback.l),
    w:finite(dims?.w, fallback.w),
    h:finite(dims?.h, fallback.h)
  };
}

function actualDescriptor(components, type) {
  const comp = components.find(item => item.type === type);
  return comp || null;
}

function buildInventory(caseObj, effective, components, branch=null) {
  const limits = effective.limits || {};
  const actualMobo = actualDescriptor(components,'motherboard');
  const actualGpu = actualDescriptor(components,'gpu');
  const actualPsu = actualDescriptor(components,'psu');
  const actualCooler = actualDescriptor(components,'aircooler');
  const actualRad = actualDescriptor(components,'radiator');

  const branchBoard = String(branch?.mobo || '').toUpperCase();
  const branchPsu = String(branch?.psu || '').toUpperCase();
  const maxBoard = BOARD_NATIVE[branchBoard] ? branchBoard : largestSupportedMotherboard(caseObj);
  const maxPsu = PSU_NATIVE[branchPsu] ? branchPsu : largestSupportedPsu(caseObj);
  const mobo = actualMobo || {
    type:'motherboard', actual:false, formFactor:maxBoard,
    dims:cloneDims(BOARD_NATIVE[maxBoard], BOARD_NATIVE.ITX)
  };
  const gpu = actualGpu || {
    type:'gpu', actual:false,
    dims:{l:finite(limits.maxGPU,300)||300,w:finite(limits.maxGPUT,50)||50,h:finite(limits.maxGPUH,137)||137}
  };
  const psu = actualPsu || {
    type:'psu', actual:false, formFactor:maxPsu,
    dims:cloneDims(PSU_NATIVE[maxPsu], PSU_NATIVE.SFX)
  };
  const cooler = actualCooler || {
    type:'aircooler', actual:false,
    dims:{l:95,w:95,h:finite(limits.maxCPU,47)||47}
  };
  const liquidBranch = String(branch?.cooling || '') === 'liquid';
  // Only selected AIO hardware is materialised. The CASE'S supported cooling envelope is
  // reserved separately below even for an air-cooled build, because real motherboard/PSU
  // mounts do not jump to a different place just because the user has not installed the
  // radiator the chassis was designed to support.
  const radiator = actualRad || null;

  // Structural reservations use the active logical branch, not an impossible union of
  // every mutually exclusive maximum. GPU/cooler/AIO envelopes remain capability-sized.
  const cap = structuralCapabilityEnvelope(effective);
  const reserve = {
    gpu: {
      type:'gpu', actual:false,
      dims:{
        l:finite(cap.gpu.l,0)||finite(gpu.dims?.l,300),
        w:finite(cap.gpu.t,0)||finite(gpu.dims?.w,50),
        h:finite(cap.gpu.h,0)||finite(gpu.dims?.h,137)
      }
    },
    cooler: {
      type:'aircooler', actual:false,
      dims:{l:95,w:95,h:finite(cap.cpu.h,0)||finite(cooler.dims?.h,47)}
    },
    radiator: cap.radiator ? (()=>{
      const reserveSize=finite(branch?.reserveRad,0)||finite(branch?.rad,0)||cap.radiator.size;
      const representative=radiatorRepresentative(reserveSize);
      // When an AIO is actually selected, its catalog radiator body is physical truth. The
      // nominal size table is only for an unselected capability reservation.
      const useActual=!!actualRad && String(branch?.cooling||'')==='liquid';
      return {
        type:'radiator', actual:false, radiatorSize:reserveSize,
        dims:useActual?cloneDims(actualRad.dims,representative):representative,
        radFan:branch?.reserveRadFan || branch?.radFan || actualRad?.radFan || effective?.spatialHints?.radFanProfile || 'standard',
        fanThickness:finite(branch?.reserveFanThickness,finite(branch?.fanThickness,finite(actualRad?.fanThickness,finite(effective?.spatialHints?.radFanThickness,25)))),
        fanSize:finite(actualRad?.fanSize,0),
        fanCount:finite(actualRad?.fanCount,0),
        fanSegments:Array.isArray(actualRad?.fanSegments) ? actualRad.fanSegments.map(segment=>({...segment})) : [],
        anchor:useActual && actualRad?.anchor ? {...actualRad.anchor,position:Array.isArray(actualRad.anchor.position)?[...actualRad.anchor.position]:actualRad.anchor.position} : null,
        dimensionSource:useActual?(actualRad?.dimensionSource||'selected_catalog'):'capability_representative'
      };
    })() : null
  };

  return {mobo,gpu,psu,cooler,radiator,reserve,capabilityEnvelope:cap,configurationBranch:branch};
}

function candidate(type,id,position,orientationClass,mount,priority=0,extra={}) {
  return {type,id,position,orientationClass,mount,priority,...extra};
}

function placementBox(desc, placement) {
  const local = componentLocalSize(desc.type, desc.dims);
  const size = orientedSize(local, placement.orientationClass);
  let center = placement.position;
  if (desc.type === 'aircooler') {
    const baseToCenter = transformPointByClass([0, finite(desc.dims.h,47)/2, 0], placement.orientationClass);
    center = [placement.position[0]+baseToCenter[0],placement.position[1]+baseToCenter[1],placement.position[2]+baseToCenter[2]];
  }
  return {center,size,bounds:boundsFromCenterSize(center,size)};
}

function moboSocket(desc, placement) {
  return transformLocalPoint(socketLocalForMotherboardStandard(desc.dims, desc.formFactor, (MOTHERBOARD_PCB_THICKNESS/2)+MOTHERBOARD_SOCKET_DECK_HEIGHT), placement.position, placement.orientationClass);
}

function coolerPlacement(inventory, moboPlacement, quarterTurn=false) {
  const orientationClass=quarterTurn?composeOrientationClasses(moboPlacement.orientationClass,4):moboPlacement.orientationClass;
  return candidate('aircooler',quarterTurn?'cpu_socket_q90':'cpu_socket',moboSocket(inventory.mobo,moboPlacement),orientationClass,'motherboard_socket',quarterTurn?92:100,{motherboardNormalInherited:true,quarterTurn:!!quarterTurn});
}

function coolerReservePlacement(inventory, moboPlacement, quarterTurn=false) {
  const orientationClass=quarterTurn?composeOrientationClasses(moboPlacement.orientationClass,4):moboPlacement.orientationClass;
  return candidate('aircooler',quarterTurn?'cpu_socket_reserved_q90':'cpu_socket_reserved',moboSocket(inventory.mobo,moboPlacement),orientationClass,'motherboard_socket_reserved',120,{structuralReservation:true,motherboardNormalInherited:true,quarterTurn:!!quarterTurn});
}

function motherboardKeepout(inventory, moboPlacement) {
  const depth=Math.max(28,finite(inventory?.mobo?.dims?.h,44));
  const desc={
    type:'motherboard_keepout',actual:false,structuralReservation:true,
    dims:{l:finite(inventory?.mobo?.dims?.l,170),w:finite(inventory?.mobo?.dims?.w,170),h:depth}
  };
  // Motherboard local +Y is the component side / inward normal in our tray orientations.
  // Offset by half the keepout depth so the envelope starts at the PCB rather than being
  // centred through the case wall.
  const offset=transformPointByClass([0,depth/2,0],moboPlacement.orientationClass);
  const placement=candidate('motherboard_keepout',`${moboPlacement.id}_component_keepout`,[
    moboPlacement.position[0]+offset[0],
    moboPlacement.position[1]+offset[1],
    moboPlacement.position[2]+offset[2]
  ],moboPlacement.orientationClass,'motherboard_component_keepout',120,{structuralReservation:true,motherboardNormalInherited:true});
  return {desc,placement};
}

function boundsInsidePenalty(inner, box, weight = 3.0) {
  return overflowDistance(inner,box) * weight;
}

function candidateInside(inner, desc, placement, tolerance=1.5) {
  return overflowDistance(inner,placementBox(desc,placement).bounds) <= tolerance;
}

function regionOverflow(box, region) {
  return overflowDistance(region, box);
}

function collisionPenalty(item, occupied, {virtualScale=0.25}={}) {
  let penalty=0;
  const a=placementBox(item.desc,item.placement).bounds;
  for(const other of occupied){
    if(!other?.desc||!other?.placement)continue;
    const pair=new Set([item.desc.type,other.desc.type]);
    if(pair.has('motherboard')&&pair.has('gpu'))continue; // PCIe relation intentionally meets the board plane.
    if(pair.has('motherboard')&&pair.has('aircooler'))continue;
    const b=placementBox(other.desc,other.placement).bounds;
    const ratio=overlapRatio(a,b);
    if(!ratio)continue;
    const bothActual=!!item.desc.actual&&!!other.desc.actual;
    penalty += ratio * (bothActual ? 150 : 150*virtualScale);
  }
  return penalty;
}

function boundsIntersection(a,b) {
  if (!a || !b) return null;
  const min=[0,1,2].map(i=>Math.max(a.min[i],b.min[i]));
  const max=[0,1,2].map(i=>Math.min(a.max[i],b.max[i]));
  return min.every((v,i)=>max[i]>v) ? {min,max} : null;
}

function boundsSize3(box) {
  return [0,1,2].map(i=>Math.max(0,box.max[i]-box.min[i]));
}

function expandBounds(box, amount=1.5) {
  return {min:box.min.map(v=>v-amount),max:box.max.map(v=>v+amount)};
}

function subtractBounds(free, obstacle) {
  const hit=boundsIntersection(free,obstacle);
  if(!hit)return[free];
  const out=[];
  const [fx0,fy0,fz0]=free.min,[fx1,fy1,fz1]=free.max;
  const [ix0,iy0,iz0]=hit.min,[ix1,iy1,iz1]=hit.max;
  // Six non-overlapping slabs around the intersection. Later obstacle subtraction can split
  // these further; tiny slivers are discarded by residualCavities().
  if(ix0>fx0)out.push({min:[fx0,fy0,fz0],max:[ix0,fy1,fz1]});
  if(ix1<fx1)out.push({min:[ix1,fy0,fz0],max:[fx1,fy1,fz1]});
  if(iy0>fy0)out.push({min:[ix0,fy0,fz0],max:[ix1,iy0,fz1]});
  if(iy1<fy1)out.push({min:[ix0,iy1,fz0],max:[ix1,fy1,fz1]});
  if(iz0>fz0)out.push({min:[ix0,iy0,fz0],max:[ix1,iy1,iz0]});
  if(iz1<fz1)out.push({min:[ix0,iy0,iz1],max:[ix1,iy1,fz1]});
  return out;
}

function residualCavities(bounds, occupied, region=null) {
  const root=region ? boundsIntersection(bounds,region) : bounds;
  if(!root)return[];
  let free=[root];
  for(const item of occupied||[]){
    if(!item?.desc||!item?.placement)continue;
    const physical=placementBox(item.desc,item.placement).bounds;
    const clearance=item.desc.actual?0.25:0.0;
    const obstacle=boundsIntersection(root,expandBounds(physical,clearance));
    if(!obstacle)continue;
    free=free.flatMap(box=>subtractBounds(box,obstacle));
    if(free.length>96){
      free.sort((a,b)=>boundsSize3(b).reduce((x,y)=>x*y,1)-boundsSize3(a).reduce((x,y)=>x*y,1));
      free=free.slice(0,96);
    }
  }
  return free.filter(box=>{
    const size=boundsSize3(box);
    return size[0]>=24&&size[1]>=24&&size[2]>=24;
  });
}

function touchingFaces(box,bounds,tolerance=2.5){
  const faces=[];
  if(Math.abs(box.min[0]-bounds.min[0])<=tolerance)faces.push('left');
  if(Math.abs(box.max[0]-bounds.max[0])<=tolerance)faces.push('right');
  if(Math.abs(box.min[1]-bounds.min[1])<=tolerance)faces.push('bottom');
  if(Math.abs(box.max[1]-bounds.max[1])<=tolerance)faces.push('top');
  if(Math.abs(box.min[2]-bounds.min[2])<=tolerance)faces.push('rear');
  if(Math.abs(box.max[2]-bounds.max[2])<=tolerance)faces.push('front');
  return faces;
}

function uniqueNumbers(values, epsilon=0.35){
  const out=[];
  for(const raw of values){
    const v=Number(raw); if(!Number.isFinite(v))continue;
    if(!out.some(x=>Math.abs(x-v)<=epsilon))out.push(v);
  }
  return out;
}

function residualAxisCenters(bounds, activeRegion, occupied, axis, extent) {
  const low=activeRegion.min[axis]+extent/2;
  const high=activeRegion.max[axis]-extent/2;
  if(high<low-1.0)return[];
  const values=[low,high,(low+high)/2];
  const gap=0.35;
  for(const item of occupied||[]){
    if(!item?.desc||!item?.placement)continue;
    const obstacle=boundsIntersection(activeRegion,placementBox(item.desc,item.placement).bounds);
    if(!obstacle)continue;
    values.push(obstacle.min[axis]-extent/2-gap,obstacle.max[axis]+extent/2+gap);
  }
  return uniqueNumbers(values).filter(v=>v>=low-1.0&&v<=high+1.0).map(v=>clamp(v,low,high));
}

function facesForPlacement(box,bounds,tolerance=1.5){
  const faces=[];
  if(Math.abs(box.min[0]-bounds.min[0])<=tolerance)faces.push('left');
  if(Math.abs(box.max[0]-bounds.max[0])<=tolerance)faces.push('right');
  if(Math.abs(box.min[1]-bounds.min[1])<=tolerance)faces.push('bottom');
  if(Math.abs(box.max[1]-bounds.max[1])<=tolerance)faces.push('top');
  if(Math.abs(box.min[2]-bounds.min[2])<=tolerance)faces.push('rear');
  if(Math.abs(box.max[2]-bounds.max[2])<=tolerance)faces.push('front');
  return faces;
}

function psuCandidates(bounds, desc, family, occupied, region=null, effective={}) {
  const policy=String(effective?.spatialHints?.psuOrientationPolicy || 'horizontal').toLowerCase();
  const activeRegion=region?boundsIntersection(bounds,region):bounds;
  if(!activeRegion)return[];

  const enumerate=(orientations,edgeFallback=false)=>{
    const out=[];
    let serial=0;
    for(const orientationClass of orientations){
      const size=orientedSize(componentLocalSize('psu',desc.dims),orientationClass);
      const xs=residualAxisCenters(bounds,activeRegion,occupied,0,size[0]);
      const ys=residualAxisCenters(bounds,activeRegion,occupied,1,size[1]);
      const zs=residualAxisCenters(bounds,activeRegion,occupied,2,size[2]);
      for(const x of xs)for(const y of ys)for(const z of zs){
        const upright=PSU_UPRIGHT_ORIENTATIONS.includes(orientationClass);
        const probe=candidate('psu',`psu_residual_${edgeFallback?'edge_':''}${serial++}_o${orientationClass}`,[x,y,z],orientationClass,'residual',0,{
          residualCavity:true,
          orientationPolicy:policy,
          psuUpright:upright,
          edgeOrientationFallback:!!edgeFallback
        });
        const box=placementBox(desc,probe).bounds;
        const outside=boundsInsidePenalty(bounds,box,8);
        const regionOutside=region?regionOverflow(box,activeRegion)*12:0;
        if(outside>0.01||regionOutside>0.01)continue;
        const collision=collisionPenalty({desc,placement:probe},occupied);
        if(collision>0.01)continue;
        const faces=facesForPlacement(box,bounds);
        if(!faces.length)continue;

        let mountFace='front';
        if(family==='console') mountFace=faces.includes('bottom')?'bottom':faces.includes('front')?'front':faces.includes('rear')?'rear':faces[0];
        else if(family==='sandwich') mountFace=faces.includes('bottom')?'bottom':faces.includes('front')?'front':faces.includes('rear')?'rear':faces[0];
        else mountFace=faces.includes('front')?'front':faces.includes('right')?'right':faces.includes('left')?'left':faces.includes('bottom')?'bottom':faces[0];
        const cableFace = mountFace==='rear'?'front':mountFace==='front'?'rear':mountFace==='bottom'?'top':'interior';
        probe.mount=`residual_${mountFace}`;
        probe.mountFace=mountFace;
        probe.cableFace=cableFace;
        probe.boundaryFaces=faces;

        const centerDistance=Math.sqrt((x/Math.max(1,bounds.max[0]-bounds.min[0]))**2+(y/Math.max(1,bounds.max[1]-bounds.min[1]))**2+(z/Math.max(1,bounds.max[2]-bounds.min[2]))**2);
        let semantic=faces.length*4-centerDistance*1.5;
        if(family==='reference'){
          const broadAtx=policy==='reference_atx_residual';
          // ATX in reference layouts is a broad residual-volume problem. Front/lower remains
          // a preference, not a forced mount. Compact SFX/SFX-L keeps the stronger front bias.
          if(faces.includes('front'))semantic+=broadAtx?12:40;
          if(faces.includes('bottom'))semantic+=broadAtx?20:10;
          if(faces.includes('right')||faces.includes('left'))semantic+=broadAtx?12:8;
          if(faces.includes('rear'))semantic+=broadAtx?4:-24;
          if(faces.includes('top'))semantic-=18;
          // Front-hung SFX brackets benefit from the shallowest front/back depth. Floor
          // mounts benefit from the lowest Y profile. This chooses orientation from geometry
          // instead of assuming every reference case has a bottom PSU platform.
          const maxExtent=Math.max(size[0],size[1],size[2],1);
          if(!broadAtx && (faces.includes('front')||faces.includes('rear')))semantic+=(1-size[2]/maxExtent)*18;
          if(faces.includes('bottom'))semantic+=(1-size[1]/maxExtent)*(broadAtx?12:8);
          // Large-board + SFX reference layouts commonly keep the same canonical PSU axes
          // but turn its facing toward the case front. Do not rotate to a different box
          // permutation; only prefer the FRONT-facing sign-equivalent classes.
          const largeBoardSmallPsu=['MATX','ATX'].includes(String(effective?.configurationBranch?.mobo||'').toUpperCase())
            && ['SFX','SFX-L'].includes(String(effective?.configurationBranch?.psu||'').toUpperCase());
          if(largeBoardSmallPsu && faces.includes('front')){
            if(PSU_FRONT_FACING_ORIENTATIONS.includes(orientationClass))semantic+=26;
            else if(PSU_HORIZONTAL_ORIENTATIONS.includes(orientationClass))semantic-=4;
          }
          // Inverted reference mirrors the service side: motherboard + PSU to the left,
          // leaving the right-side vertical cooling plane free. This is a topology-level
          // handedness preference, not a case-name exception.
          if(effective?.modeTraits?.flipped){
            if(faces.includes('left'))semantic+=24;
            if(faces.includes('right'))semantic-=10;
            const halfWidth=Math.max(1,(bounds.max[0]-bounds.min[0])/2);
            semantic+=Math.max(-10,Math.min(10,(-x/halfWidth)*10));
          }
        }else if(family==='sandwich'){
          if(faces.includes('bottom'))semantic+=20;
          if(faces.includes('front'))semantic+=22;
          if(faces.includes('rear'))semantic+=8;
          if(faces.includes('top'))semantic-=24;
          const innerWidth=Math.max(1,bounds.max[0]-bounds.min[0]);
          const crossShare=size[0]/innerWidth;
          // X is the cross-spine axis. Prefer the narrow PSU face here; class 17 maps the
          // 63.5/86 mm thickness to X while leaving the long PSU axis horizontal.
          semantic+=(1-Math.min(1.4,crossShare))*34;
          if(orientationClass===17)semantic+=18;
          const tallVertical=isUprightSandwich(effective);
          if(tallVertical){
            if(upright)semantic+=20;
            else if(orientationClass===17)semantic+=6;
          }
        }else if(family==='console'){
          if(faces.includes('bottom'))semantic+=30;
          if(faces.includes('front')||faces.includes('rear'))semantic+=20;
          if(faces.includes('top'))semantic-=30;
          const innerWidth=Math.max(1,bounds.max[0]-bounds.min[0]);
          const widthShare=size[0]/innerWidth;
          if(upright) semantic += widthShare<0.72 ? 18 : -4;
          else if(widthShare>0.82) semantic -= 24;
        }
        if(policy==='horizontal_flipped' && orientationClass===PSU_HORIZONTAL_FLIPPED_ORIENTATIONS[0])semantic+=24;
        if(policy==='front_facing' && orientationClass===PSU_FRONT_FACING_ORIENTATIONS[0])semantic+=28;
        if(policy==='sandwich_narrow_x' && orientationClass===PSU_NARROW_X_ORIENTATIONS[0])semantic+=28;
        if(policy==='sandwich_vertical_flexible' && orientationClass===PSU_NARROW_X_ORIENTATIONS[0])semantic+=12;
        if(policy==='horizontal' && family!=='sandwich' && orientationClass===PSU_HORIZONTAL_ORIENTATIONS[0])semantic+=6;
        if(edgeFallback)semantic-=18;
        probe.fitScore=semantic;
        probe.priority=Math.round(100+semantic);
        probe.diagnostics={outside,regionOutside,collision,faces,policy,upright,edgeFallback};
        out.push(probe);
      }
    }
    return out.sort((a,b)=>b.fitScore-a.fitScore).slice(0,8);
  };

  const primaryOrientations=policy==='horizontal_flipped'
    ? PSU_HORIZONTAL_FLIPPED_ORIENTATIONS
    : policy==='front_facing'
      ? PSU_FRONT_FACING_ORIENTATIONS
    : policy==='sandwich_narrow_x'
      ? PSU_NARROW_X_ORIENTATIONS
      : policy==='sandwich_vertical_flexible'
        ? [...PSU_NARROW_X_ORIENTATIONS,...PSU_UPRIGHT_ORIENTATIONS]
        : policy==='upright'
          ? PSU_UPRIGHT_ORIENTATIONS
          : policy==='horizontal_or_upright'
            ? [...PSU_HORIZONTAL_ORIENTATIONS,...PSU_UPRIGHT_ORIENTATIONS]
            : PSU_HORIZONTAL_ORIENTATIONS;
  const primary=enumerate(primaryOrientations,false);
  if(primary.length)return primary;

  // Tall vertical-sandwich cases such as Meshroom can genuinely require the PSU length to
  // run vertically. It is an edge fallback only: never offer these poses when a normal flat
  // PSU candidate already exists. Console already includes upright poses in its primary set.
  const verticalSandwich=family==='sandwich' && isUprightSandwich(effective);
  const allowUprightFallback=effective?.spatialHints?.psuAllowUprightFallback===true;
  if(verticalSandwich && allowUprightFallback && !policy.includes('upright')) return enumerate(PSU_UPRIGHT_ORIENTATIONS,true);
  return [];
}

function radiatorBaseMount(mount) {
  return String(mount || '').replace(/_upper$/, '');
}

function radiatorPlacementForMount(bounds, desc, mount, orientationClass) {
  const [sx,sy,sz]=orientedSize(componentLocalSize('radiator',desc.dims),orientationClass);
  const baseMount=radiatorBaseMount(mount);
  const upper=String(mount||'').endsWith('_upper');
  let position=[0,0,0];
  if(baseMount==='top')position=[0,bounds.max[1]-sy/2,0];
  else if(baseMount==='bottom')position=[0,bounds.min[1]+sy/2,0];
  else if(baseMount==='side_right')position=[bounds.max[0]-sx/2,upper?bounds.max[1]-sy/2:0,0];
  else if(baseMount==='side_left')position=[bounds.min[0]+sx/2,upper?bounds.max[1]-sy/2:0,0];
  else if(baseMount==='front')position=[0,upper?bounds.max[1]-sy/2:0,bounds.max[2]-sz/2];
  else if(baseMount==='rear')position=[0,upper?bounds.max[1]-sy/2:0,bounds.min[2]+sz/2];
  return candidate('radiator',`rad_${mount}_o${orientationClass}`,position,orientationClass,mount,0,{upperZone:upper});
}

function radiatorOrientationForMount(mount) {
  const baseMount=radiatorBaseMount(mount);
  const upper=String(mount||'').endsWith('_upper');
  if(baseMount==='side_left'||baseMount==='side_right')return upper?17:1; // upper vertical plane: long axis -> Y, thin -> X
  if(baseMount==='front'||baseMount==='rear')return 16;                  // long axis -> Y, thin -> Z for vertical planes
  return 0;                                                              // top/bottom: thin axis -> Y
}

function coolingAssemblyDiagnostics(bounds, desc, placement, occupied, effective) {
  const assembly=coolingAssemblyReservations(bounds,desc,placement,effective);
  let outside=0, collision=0;
  for(const item of assembly){
    outside += boundsInsidePenalty(bounds,placementBox(item.desc,item.placement).bounds,8);
    collision += collisionPenalty({desc:item.desc,placement:item.placement},occupied,{virtualScale:1});
  }
  return {assembly,outside,collision};
}

function radiatorAxisValues(bounds, size, basePosition, axis, occupied=[]) {
  const low=bounds.min[axis]+size[axis]/2;
  const high=bounds.max[axis]-size[axis]/2;
  if(high<low-0.01)return[];
  const base=clamp(basePosition[axis],low,high);
  const probes=[base,low,high,(low+high)/2];
  const travel=Math.max(0,high-low);
  if(travel>8){
    const step=Math.min(20,travel/3);
    probes.push(base-step,base+step);
  }
  // Probe immediately before/after real obstacles. This is what lets a 240 mm radiator
  // slide along its existing bracket instead of changing mount plane when a front SFX PSU
  // consumes part of the usable span.
  for(const item of occupied||[]){
    if(!item?.desc||!item?.placement)continue;
    const obstacle=boundsIntersection(bounds,placementBox(item.desc,item.placement).bounds);
    if(!obstacle)continue;
    probes.push(obstacle.min[axis]-size[axis]/2-1.5,obstacle.max[axis]+size[axis]/2+1.5);
  }
  return uniqueNumbers(probes.map(value=>clamp(value,low,high))).filter(value=>value>=low-0.5&&value<=high+0.5);
}

function radiatorPlacementVariants(bounds, desc, mount, orientationClass, occupied=[]) {
  const base=radiatorPlacementForMount(bounds,desc,mount,orientationClass);
  const size=orientedSize(componentLocalSize('radiator',desc.dims),orientationClass);
  const baseMount=radiatorBaseMount(mount);
  const upper=String(mount||'').endsWith('_upper');
  const normalAxis=(baseMount==='top'||baseMount==='bottom')?1:(baseMount==='side_left'||baseMount==='side_right')?0:2;
  let movableAxes=[0,1,2].filter(axis=>axis!==normalAxis);
  // An "upper" mount is an upper rail/zone, not permission to slide the radiator down the
  // whole side of a tall case. Keep Y fixed and only translate along the remaining rail.
  if(upper)movableAxes=movableAxes.filter(axis=>axis!==1);
  if(!movableAxes.length)return[base];

  const axisValues=movableAxes.map(axis=>[axis,radiatorAxisValues(bounds,size,base.position,axis,occupied)]);
  if(axisValues.some(([,values])=>!values.length))return[base];
  const out=[];
  let serial=0;
  const recurse=(idx,position)=>{
    if(idx>=axisValues.length){
      const displacement=Math.sqrt(movableAxes.reduce((sum,axis)=>sum+((position[axis]-base.position[axis])**2),0));
      out.push({...base,id:`${base.id}_slide${serial++}`,position:[...position],slideDistance:displacement,relationalTranslation:displacement>0.1});
      return;
    }
    const [axis,values]=axisValues[idx];
    for(const value of values){const next=[...position];next[axis]=value;recurse(idx+1,next);}
  };
  recurse(0,[...base.position]);
  return out.sort((a,b)=>a.slideDistance-b.slideDistance).slice(0,24);
}

function anchoredRadiatorCandidate(desc) {
  const anchor=desc?.anchor;
  if(!anchor?.locked || !Array.isArray(anchor.position))return null;
  return candidate('radiator','rad_user_locked_anchor',[...anchor.position],Number(anchor.orientationClass)||0,anchor.mount||'user_anchor',1000,{
    userLockedAnchor:true,
    relationalTranslation:true,
    anchorSource:anchor.source||'user_locked_runtime'
  });
}

function radiatorCandidates(bounds, desc, family, occupied, effective={}) {
  if(!desc)return[];
  const anchored=anchoredRadiatorCandidate(desc);
  if(anchored){
    const diagnostics=coolingAssemblyDiagnostics(bounds,desc,anchored,occupied,effective);
    anchored.fitScore=1000;
    anchored.priority=1100;
    anchored.structuralReservation=!desc.actual;
    anchored.coolingAssembly=diagnostics.assembly;
    anchored.diagnostics={outside:diagnostics.outside,collision:diagnostics.collision,mountOrder:-1,assemblyDepth:coolingAssemblyDepth(effective,desc),userLockedAnchor:true};
    // The locked assembly is an input constraint. If it is still inside the chassis, keep it
    // even if the CURRENT dependent board pose collides; the outer solve is responsible for
    // moving the dependent component around the user anchor.
    return diagnostics.outside<=0.01?[anchored]:[];
  }
  const sourceRadiatorSpec={
    radiatorThickness:finite(desc?.dims?.h,0),
    stackThickness:coolingAssemblyDepth(effective,desc)
  };
  const sourceMountOrder=catalogRadiatorMountPreferences(effective,desc?.radiatorSize||0,sourceRadiatorSpec);
  let order=preferredRadiatorMounts(family,effective,desc?.radiatorSize||0,sourceRadiatorSpec);
  const innerH=bounds.max[1]-bounds.min[1], innerL=bounds.max[2]-bounds.min[2], innerW=bounds.max[0]-bounds.min[0];
  const explicitMount=String(effective?.spatialHints?.radiatorMount || effective?.spatialHints?.aioMount || '').trim();
  const verticalSandwich=family==='sandwich' && !explicitMount && isUprightSandwich(effective);
  if(verticalSandwich){
    // Preserve the same "cooling at the top end" relationship without rotating the whole
    // chassis. In a tall sandwich this usually means an upper side/end plane. Explicitly
    // parsed source-note locations remain preference evidence in real chassis coordinates.
    const verticalDefaults=['side_right_upper','side_left_upper','front_upper','rear_upper','top','side_right','side_left','front','rear','bottom'];
    order=[...new Set([...sourceMountOrder,...verticalDefaults])];
  }
  const collect=(mountOrder,{strictClear=false}={})=>{
    const candidates=[];
    for(let index=0;index<mountOrder.length;index+=1){
      const mount=mountOrder[index];
      const orderIndex=Math.max(0,order.indexOf(mount));
      const orientationClass=radiatorOrientationForMount(mount);
      const variants=radiatorPlacementVariants(bounds,desc,mount,orientationClass,occupied);
      for(const p of variants){
        const diagnostics=coolingAssemblyDiagnostics(bounds,desc,p,occupied,effective);
        if(diagnostics.outside>0.01||diagnostics.collision>(strictClear?0.01:0.5))continue;
        const primaryMount = family==='reference' ? (mount==='side_right'||mount==='side_left')
          : family==='sandwich' ? (mount==='top'||String(mount).endsWith('_upper'))
            : (mount==='side_right'||mount==='side_left'||mount==='front'||mount==='rear');
        const displacementPenalty=Math.min(24,(Number(p.slideDistance)||0)*0.22);
        const semantic=100-orderIndex*11+(primaryMount?24:0)+(mount==='top'&&family==='sandwich'?20:0)+(String(mount).endsWith('_upper')&&family==='sandwich'?36:0)-displacementPenalty;
        p.fitScore=semantic;
        p.priority=Math.round(100+semantic);
        p.structuralReservation=!desc.actual;
        p.coolingAssembly=diagnostics.assembly;
        p.diagnostics={outside:diagnostics.outside,collision:diagnostics.collision,mountOrder:orderIndex,assemblyDepth:coolingAssemblyDepth(effective,desc),slideDistance:Number(p.slideDistance)||0};
        candidates.push(p);
      }
    }
    return candidates.sort((a,b)=>(b.priority||0)-(a.priority||0));
  };

  // Reference layouts retain their established side-first/top-fallback behavior when source
  // data does not identify a radiator location. When the catalogue note explicitly names a
  // compatible mount, try that hard-valid plane first (including rear/front/bottom), then fall
  // back to the proven generic reference behavior if the evidence-backed placement cannot fit.
  if(family==='reference' && !explicitMount){
    if(sourceMountOrder.length){
      const sourced=collect(sourceMountOrder);
      if(sourced.length)return sourced.slice(0,20);
    }
    const vertical=collect(order.filter(mount=>mount==='side_right'||mount==='side_left'));
    if(vertical.length)return vertical.slice(0,20);
    const top=collect(order.filter(mount=>mount==='top'),{strictClear:true});
    return top.slice(0,8);
  }

  const candidates=collect(order);
  return candidates.slice(0,20);
}

function reservationRadiatorPlacements(bounds, inventory, family, occupied, effective) {
  const reserveDesc=inventory.reserve?.radiator;
  if(!reserveDesc)return [];
  return radiatorCandidates(bounds,reserveDesc,family,occupied,effective).map(placement=>({
    desc:reserveDesc,
    placement,
    assembly:placement.coolingAssembly || coolingAssemblyReservations(bounds,reserveDesc,placement,effective)
  }));
}

function planningRadiatorPlacements(bounds, inventory, family, occupied, effective) {
  const structural=reservationRadiatorPlacements(bounds,inventory,family,occupied,effective);
  if(structural.length)return structural;
  if(!inventory.radiator)return[];
  // If a generic max-size representative cannot be mounted but the selected catalog AIO can,
  // let the real radiator body define the planning cavity rather than placing components first
  // and discovering the AIO too late.
  return radiatorCandidates(bounds,inventory.radiator,family,occupied,effective).map(placement=>({
    desc:inventory.radiator,
    placement,
    assembly:placement.coolingAssembly || coolingAssemblyReservations(bounds,inventory.radiator,placement,effective),
    actualPlanningFallback:true
  }));
}

function materializeRadiatorFromReservation(bounds, inventory, reservation, family, occupied, effective) {
  if(!inventory.radiator)return {placement:null,candidates:[],assembly:[]};
  if(reservation?.placement){
    const mount=reservation.placement.mount;
    const orientationClass=Number.isInteger(Number(reservation.placement.orientationClass))
      ? Number(reservation.placement.orientationClass)
      : radiatorOrientationForMount(mount);
    // Preserve the exact translated/anchored reservation. Re-centering the real selected
    // AIO here discards the relational repair chosen during planning.
    const p={...reservation.placement,type:'radiator',orientationClass,position:[...reservation.placement.position]};
    const diagnostics=coolingAssemblyDiagnostics(bounds,inventory.radiator,p,occupied,effective);
    if(diagnostics.outside<=0.01&&diagnostics.collision<=0.5){
      p.fitScore=reservation.placement.fitScore;
      p.priority=reservation.placement.priority;
      p.coolingAssembly=diagnostics.assembly;
      return {placement:p,candidates:[p],assembly:diagnostics.assembly};
    }
  }
  const candidates=radiatorCandidates(bounds,inventory.radiator,family,occupied,effective);
  const placement=candidates[0]||null;
  return {placement,candidates,assembly:placement?.coolingAssembly || (placement?coolingAssemblyReservations(bounds,inventory.radiator,placement,effective):[])};
}

function assemblyOccupied(reservation) {
  return reservation?.assembly || [];
}

function projectedOverlap(boxA,boxB,axes=[0,2],pad=0){
  if(!boxA||!boxB)return false;
  return axes.every(axis=>Math.min(boxA.max[axis],boxB.max[axis])+pad>Math.max(boxA.min[axis],boxB.min[axis]));
}

function ceilingBelowTopAssembly(bounds, footprintBox, radReservation, margin=2){
  let upper=bounds.max[1];
  const placement=radReservation?.placement;
  const upperAssembly=placement && (radiatorBaseMount(placement.mount)==='top' || placement.upperZone===true || String(placement.mount||'').endsWith('_upper'));
  if(!upperAssembly) return upper;
  for(const item of assemblyOccupied(radReservation)){
    const box=placementBox(item.desc,item.placement).bounds;
    // The reservation is already known to be a TOP assembly. Every radiator/fan segment
    // in that stack contributes to the local ceiling, even though the inward fan itself
    // does not touch the chassis ceiling. Side/front cooling never reaches this helper.
    const zOverlap=Math.max(0,Math.min(footprintBox.max[2],box.max[2])-Math.max(footprintBox.min[2],box.min[2]));
    if(projectedOverlap(footprintBox,box,[0,2],0.5) && zOverlap>8) upper=Math.min(upper,box.min[1]-margin);
  }
  return upper;
}

function topAlignedPlacementY(bounds,size,upper){
  const maxY=Math.min(bounds.max[1],finite(upper,bounds.max[1]));
  return maxY-size[1]/2;
}

function referenceMotherboardPlacement(bounds, inventory, traySide, gpuReservePlacement, effective) {
  const rear=bounds.min[2], floor=bounds.min[1], ceiling=bounds.max[1], right=bounds.max[0], left=bounds.min[0];
  const componentNormalX=traySide==='right'?-1:1;
  // The chiral motherboard frame makes rear-I/O the stable physical anchor. Standard
  // reference uses the RIGHT tray: component side -X + rear I/O => class 23, PCIe down.
  // A genuinely flipped reference layout moves to the LEFT tray: component side +X +
  // rear I/O => class 19, which necessarily rotates PCIe to the top as one rigid board.
  const explicitIoEdge=effective?.spatialHints?.motherboardIoEdge
    ?? effective?.spatialHints?.boardIoEdge
    ?? effective?.spatialHints?.ioEdge;
  const explicitPcieEdge=effective?.spatialHints?.motherboardPcieEdge
    ?? effective?.spatialHints?.boardPcieEdge;
  const orientationClass=explicitPcieEdge!=null
    ? motherboardOrientationForPcieEdge(componentNormalX,explicitPcieEdge)
    : motherboardOrientationClass(componentNormalX,explicitIoEdge??'rear');
  const frameSemantics=motherboardOrientationSemantics(orientationClass)||{};
  const size=orientedSize(componentLocalSize('motherboard',inventory.mobo.dims),orientationClass);
  const x=traySide==='right'?right-size[0]/2:left+size[0]/2;
  const z=rear+size[2]/2;
  const gpuBox=placementBox(inventory.reserve.gpu,gpuReservePlacement).bounds;
  const gpuMount=String(gpuReservePlacement?.mount||'');

  // Reference structure is derived from the CASE'S maximum GPU corridor, never from the
  // currently installed card and never from a provisional PSU pose. The board is the tray
  // immediately above a lower GPU corridor (or below an inverted/upper corridor). PSU XYZ is
  // solved later inside the remaining platform cavity.
  let lower=floor;
  let upper=ceiling;
  if(gpuMount.includes('lower')) {
    const preferredLower=Math.max(lower,gpuBox.max[1]+2);
    // The motherboard PCB plane may overlap the GPU's vertical projection at the PCIe
    // region; only the CPU-side keepout must stay clear. Clamp instead of forcing large
    // mATX/ATX boards entirely above the GPU thickness envelope.
    lower=Math.min(preferredLower,ceiling-size[1]);
    lower=Math.max(floor,lower);
  } else if(gpuMount.includes('upper')) {
    const preferredUpper=Math.min(upper,gpuBox.min[1]-2);
    upper=Math.max(floor+size[1],preferredUpper);
  }

  let y;
  if(gpuMount.includes('upper')) y=upper-size[1]/2;
  else y=lower+size[1]/2;

  let probePlacement=candidate('motherboard','mobo_reference_probe',[x,y,z],orientationClass,`tray_${traySide}`,0,{motherboardFrameAuthority:'chiral_rigid_board_frame_v3',componentNormalX,ioEdge:frameSemantics.ioEdge||'rear',pcieEdge:frameSemantics.pcieEdge||null,motherboardChamber:traySide,frameSource:explicitPcieEdge!=null?'structured_pcie_edge':(explicitIoEdge!=null?'structured_io_edge':'reference_rear_io')});
  const coolerProbe=coolerReservePlacement(inventory,probePlacement);
  const coolerProbeBox=placementBox(inventory.reserve.cooler,coolerProbe).bounds;
  // In a normal lower-GPU reference layout, keep the maximum CPU projection out of the GPU
  // capability corridor. In an upper/inverted layout the same relationship is mirrored.
  if(overlapRatio(coolerProbeBox,gpuBox)>0.005){
    if(gpuMount.includes('upper')) {
      const shift=Math.max(0,coolerProbeBox.max[1]-gpuBox.min[1]+2);
      upper=Math.max(floor+size[1],upper-shift);
      upper=Math.min(ceiling,upper);
      y=upper-size[1]/2;
    } else {
      const shift=Math.max(0,gpuBox.max[1]-coolerProbeBox.min[1]+2);
      lower=Math.min(ceiling-size[1],lower+shift);
      lower=Math.max(floor,lower);
      y=lower+size[1]/2;
    }
    probePlacement={...probePlacement,position:[x,y,z]};
  }

  const placement=candidate('motherboard',`mobo_tray_${traySide}`,[x,y,z],orientationClass,`tray_${traySide}`,100,{
    structuralOrder:'gpu_capability_board_then_cooling_then_psu_pose',
    boardLowerY:y-size[1]/2,
    boardUpperY:y+size[1]/2,
    boardUpperLimit:ceiling,
    gpuCapabilityMount:gpuMount,
    motherboardFrameAuthority:'chiral_rigid_board_frame_v3',
    componentNormalX,
    ioEdge:frameSemantics.ioEdge||'rear',
    pcieEdge:frameSemantics.pcieEdge||null,
    motherboardChamber:traySide,
    frameSource:explicitPcieEdge!=null?'structured_pcie_edge':(explicitIoEdge!=null?'structured_io_edge':'reference_rear_io')
  });
  const box=placementBox(inventory.mobo,placement).bounds;
  placement.structuralFit=box.max[1]<=ceiling+1.5 && box.min[1]>=floor-1.5;
  placement.structuralOverflow=Math.max(0,box.max[1]-ceiling)+Math.max(0,floor-box.min[1]);
  return placement;
}

function referencePsuRegion(bounds, inventory, moboPlacement, gpuReservePlacement, effective) {
  const rear=bounds.min[2], front=bounds.max[2], floor=bounds.min[1], ceiling=bounds.max[1], left=bounds.min[0], right=bounds.max[0];
  const policy=String(effective?.spatialHints?.psuRegionPolicy||'reference_front_residual').toLowerCase();
  const orientationPolicy=String(effective?.spatialHints?.psuOrientationPolicy||'horizontal').toLowerCase();
  const orientationPool=orientationPolicy==='horizontal_flipped' ? PSU_HORIZONTAL_FLIPPED_ORIENTATIONS : PSU_HORIZONTAL_ORIENTATIONS;
  const depths=orientationPool.map(cls=>orientedSize(componentLocalSize('psu',inventory.psu.dims),cls)[2]).filter(Number.isFinite);
  const shallowDepth=Math.min(...depths,finite(inventory.psu?.dims?.h,100));

  if(policy==='reference_atx_residual'){
    // A declared ATX PSU in a reference enclosure is not a front-bracket SFX relationship.
    // Let hard geometry search the complete residual chassis; scoring still prefers sensible
    // boundary/floor positions. This avoids false front-mount failures in roomy mATX cases.
    return {min:[left,floor,rear],max:[right,ceiling,front]};
  }

  if(policy==='large_board_front_lateral'){
    // Large-board layouts need a broader front/lateral search, but not an artificial lower
    // half restriction. The board/keepout and cooling reservations already protect occupied Y.
    const depth=Math.max(shallowDepth+10,(front-rear)*0.64);
    return {min:[left,floor,Math.max(rear,front-depth)],max:[right,ceiling,front]};
  }

  // Generic reference PSU mounts are usually front-boundary consumers. Search the complete
  // vertical front strip and let hard collision geometry decide the usable height.
  const depth=Math.max(shallowDepth+10,(front-rear)*0.46);
  return {min:[left,floor,Math.max(rear,front-depth)],max:[right,ceiling,front]};
}

function referenceGpuPlacement(bounds, inventory, traySide, level, reserve=false, reservePlacement=null, pcieWorldAnchor=null) {
  const rear=bounds.min[2], floor=bounds.min[1], ceiling=bounds.max[1], right=bounds.max[0], left=bounds.min[0];
  const desc=reserve?inventory.reserve.gpu:inventory.gpu;
  let orientationClass,position,mount;
  if(level==='vertical'){
    orientationClass=traySide==='right'?6:12;
    const size=orientedSize(componentLocalSize('gpu',desc.dims),orientationClass);
    position=[traySide==='right'?left+size[0]/2:right-size[0]/2,floor+size[1]/2,rear+size[2]/2];
    mount=reserve?'vertical_gpu_panel_reserved':'vertical_gpu_front';
  }else{
    orientationClass=level==='upper'?21:17;
    const size=orientedSize(componentLocalSize('gpu',desc.dims),orientationClass);
    // Horizontal reference GPUs register to the motherboard's primary PCIe connector.
    // ITX used to hide the old floor/edge approximation because its x16 slot is only ~5 mm
    // from the PCB bottom. MATX/ATX expose the error: their primary connector is much higher.
    // Anchor BOTH the capability reservation and the real GPU to the same slot plane so a
    // thinner installed card cannot drift away from the motherboard relationship.
    const hasPcieAnchor=Number.isFinite(Number(pcieWorldAnchor?.[1]));
    const pcieY=hasPcieAnchor?Number(pcieWorldAnchor[1]):null;
    // The PCIe connector does NOT run through the middle of a graphics card's thickness.
    // It meets the card at its motherboard-facing PCB/backplate face. Anchoring the GPU centre
    // directly to the connector therefore lifted a 60 mm card by ~30 mm. Register the facing
    // face to the slot instead: lower/FANS-DOWN cards extend below the slot, while an inverted
    // upper/FANS-UP card extends above it. `size[1]` is the oriented world-space thickness.
    let y=hasPcieAnchor
      ? pcieY + (level==='upper'?size[1]/2:-size[1]/2)
      : (level==='upper'?ceiling-size[1]/2:floor+size[1]/2);

    if(!pcieWorldAnchor && !reserve && reservePlacement){
      // Reference GPUs are PCIe/motherboard registered. The capability reservation still
      // occupies the case's complete published max-thickness corridor, but a thinner real
      // card hugs the motherboard-facing face of that corridor. Any unused thickness is
      // therefore left on the opposite chassis side for bottom/top fans and other residual
      // volume rather than opening an artificial gap between GPU and motherboard.
      const reserveBox=placementBox(inventory.reserve.gpu,reservePlacement).bounds;
      y=level==='upper'
        ? reserveBox.min[1]+size[1]/2
        : reserveBox.max[1]-size[1]/2;
    }

    position=[traySide==='right'?right-size[0]/2:left+size[0]/2,y,rear+size[2]/2];
    mount=reserve?`pcie_${level}_reserved`:`pcie_${level}`;
  }
  return candidate('gpu',`gpu_${level}_${reserve?'reserved_':''}${traySide}`,position,orientationClass,mount,reserve?120:(level==='upper'?75:100),reserve
    ? {structuralReservation:true,pcieRegistered:!!pcieWorldAnchor,pcieAnchorWorld:pcieWorldAnchor||null,pcieAnchorRelation:pcieWorldAnchor?'motherboard_facing_gpu_face':'none'}
    : {pcieRegistered:!!pcieWorldAnchor,pcieAnchorWorld:pcieWorldAnchor||null,pcieAnchorRelation:pcieWorldAnchor?'motherboard_facing_gpu_face':'none',relationship:pcieWorldAnchor?'motherboard_primary_pcie':'topology_fallback'});
}

function motherboardPcieWorldAnchor(inventory,moboPlacement){
  if(!inventory?.mobo||!moboPlacement?.position)return null;
  const local=pcieLocalForMotherboardStandard(inventory.mobo.dims,inventory.mobo.formFactor||'ITX',0);
  return transformLocalPoint(local,moboPlacement.position,moboPlacement.orientationClass);
}

function distinctPlacementCandidates(candidates, maxCount=6) {
  const out=[];
  const seen=new Set();
  for(const item of candidates||[]){
    if(!item?.position)continue;
    const key=item.position.map(v=>Math.round(Number(v||0)*2)/2).join('|');
    if(seen.has(key))continue;
    seen.add(key);
    out.push(item);
    if(out.length>=maxCount)break;
  }
  return out;
}

function normalizedMotherboardSide(value) {
  const raw=String(value || '').trim().toLowerCase();
  if (['left','-x','negative_x','negative-x'].includes(raw)) return 'left';
  if (['right','+x','positive_x','positive-x'].includes(raw)) return 'right';
  return '';
}

function referenceTraySides(effective) {
  const hinted=normalizedMotherboardSide(
    effective?.spatialHints?.motherboardTraySide
    || effective?.spatialHints?.motherboardSide
    || effective?.spatialHints?.boardSide
  );
  if (hinted) return [hinted];

  // Canonical reference uses the RIGHT tray. With the chirality-correct local board frame,
  // class 23 places physical left/rear-I/O at case rear, physical right/DIMMs toward front,
  // and primary PCIe at the floor. A real flipped/inverted mode uses the opposite LEFT tray
  // and class 19. Mirror capability alone never becomes score authority.
  const primary=effective?.modeTraits?.flipped ? 'left' : 'right';
  // `allowMotherboardMirror` describes capability only. The deterministic solver must not
  // rank two visually opposite board frames and silently choose one; a selected mode or an
  // explicit motherboardTraySide is required to change handedness.
  return [primary];
}

function referenceArrangements(h, inventory, effective) {
  const bounds=interiorBounds(h.canonical,3);
  const floor=bounds.min[1], ceiling=bounds.max[1], right=bounds.max[0], left=bounds.min[0], rear=bounds.min[2], front=bounds.max[2];
  const arrangements=[];
  const verticalGpu=modeUsesVerticalGpu(effective);
  const inverted=!!effective?.modeTraits?.flipped;
  // A flipped/inverted reference mode is a real layout flip, not an extra optional GPU
  // altitude. GPU owns the upper corridor while the motherboard moves from the canonical
  // RIGHT tray to the opposite LEFT tray; board landmarks rotate together as one rigid body.
  const gpuVariants=verticalGpu?['vertical']:(inverted?['upper']:['lower']);
  const traySides=referenceTraySides(effective);

  for(const traySide of traySides){
    for(const gpuLevel of gpuVariants){
      // The provisional maximum-GPU corridor still establishes the board's broad reference
      // vertical region. Once the motherboard pose is known, the real PCIe landmark becomes
      // authoritative for horizontal GPU altitude. This removes the ITX-only edge assumption
      // without destabilising the existing reference packing order. Vertical-riser GPU modes
      // intentionally keep their independent riser placement.
      // A reference VGPU/riser mode does NOT relocate the motherboard tray. Preserve the
      // motherboard against the chassis' native lower-GPU corridor, then solve only the GPU
      // in its vertical riser pose. Using the mode's 3-slot/4-slot thickness here would make
      // the board jump even though its mounting holes are physically unchanged.
      let boardInventory=inventory;
      let boardGpuLevel=gpuLevel;
      if(gpuLevel==='vertical' && effective?.modeTraits?.verticalGpuOnly===true){
        boardGpuLevel=effective?.modeTraits?.flipped?'upper':'lower';
        const nativeGpuT=finite(effective?.baseLimits?.maxGPUT,null);
        if(nativeGpuT!==null && nativeGpuT>0){
          boardInventory={
            ...inventory,
            reserve:{
              ...inventory.reserve,
              gpu:{...inventory.reserve.gpu,dims:{...inventory.reserve.gpu.dims,w:nativeGpuT}}
            }
          };
        }
      }
      const provisionalGpuReserve=referenceGpuPlacement(bounds,boardInventory,traySide,boardGpuLevel,true);
      const mobo=referenceMotherboardPlacement(bounds,boardInventory,traySide,provisionalGpuReserve,effective);
      const pcieAnchor=gpuLevel==='vertical'?null:motherboardPcieWorldAnchor(inventory,mobo);
      const gpuReserve=referenceGpuPlacement(bounds,inventory,traySide,gpuLevel,true,null,pcieAnchor);
      const gpu=referenceGpuPlacement(bounds,inventory,traySide,gpuLevel,false,gpuReserve,pcieAnchor);
      const cooler=coolerPlacement(inventory,mobo);
      const coolerReserve=coolerReservePlacement(inventory,mobo);
      const liquidBranch=String(inventory.configurationBranch?.cooling||effective?.configurationBranch?.cooling||'')==='liquid';
      const largeBoardSmallPsu=['MATX','ATX'].includes(String(inventory?.mobo?.formFactor||'').toUpperCase())
        && ['SFX','SFX-L'].includes(String(inventory?.psu?.formFactor||'').toUpperCase());
      const boardKeepout=motherboardKeepout(inventory,mobo);
      const structuralCore=[
        {desc:inventory.reserve.gpu,placement:gpuReserve},
        {desc:inventory.mobo,placement:mobo},
        boardKeepout,
        ...(!liquidBranch?[{desc:inventory.reserve.cooler,placement:coolerReserve}]:[])
      ];

      // Large-board + SFX + liquid is a shared-cavity problem: the front PSU bracket owns
      // its position first, then the radiator is allowed to translate ALONG its existing
      // reference mount plane around that occupied volume. This is deliberately not a
      // case-name rule and it does not unlock a different radiator plane.
      if(liquidBranch && largeBoardSmallPsu){
        const primaryRegion=referencePsuRegion(bounds,inventory,mobo,gpuReserve,effective);
        let psus=psuCandidates(bounds,inventory.psu,'reference',structuralCore,primaryRegion,effective);
        if(!psus.length){
          const fullReferenceRegion={min:[left,floor,rear],max:[right,ceiling,front]};
          psus=psuCandidates(bounds,inventory.psu,'reference',structuralCore,fullReferenceRegion,effective);
        }
        // Facing variants at one coordinate are the same physical cavity. Keep spatially
        // distinct candidates so the paired cooling search explores actual alternatives.
        const psuChoices=distinctPlacementCandidates(psus,6);
        if(!psuChoices.length)psuChoices.push(null);

        for(const psu of psuChoices){
          const sharedStructuralCore=[...structuralCore,...(psu?[{desc:inventory.psu,placement:psu}]:[])];
          const radReservations=planningRadiatorPlacements(bounds,inventory,'reference',sharedStructuralCore,effective);
          const coolingBranches=radReservations.length?radReservations:[null];
          for(const radReservation of coolingBranches){
            const actualCoolingCore=[
              {desc:inventory.mobo,placement:mobo},
              boardKeepout,
              {desc:inventory.gpu,placement:gpu},
              ...(psu?[{desc:inventory.psu,placement:psu}]:[]),
              ...(inventory.cooler?.actual?[{desc:inventory.cooler,placement:cooler}]:[])
            ];
            const radActual=materializeRadiatorFromReservation(bounds,inventory,radReservation,'reference',actualCoolingCore,effective);
            const activeAssembly=(radActual.placement&&radActual.assembly?.length)?radActual.assembly:assemblyOccupied(radReservation);
            arrangements.push({
              bounds,mobo,gpu,cooler,psu,
              // Keep the PSU/radiator pair atomic. A later mount-variant expansion must not
              // swap a different PSU underneath a radiator solved around this exact cavity.
              psuCandidates:psu?[psu]:[],radiator:radActual.placement,
              radiatorCandidates:radActual.candidates,spineX:null,
              configurationBranch:inventory.configurationBranch,
              reservations:{
                gpu:gpuReserve,
                cpu:coolerReserve,
                motherboardKeepout:boardKeepout.placement,
                psuPlatform:null,
                radiator:radReservation?.placement||null,
                radiatorAssembly:activeAssembly,
                structuralRadiatorAssembly:radReservation?.assembly||[],
                solveOrder:['gpu_capability_envelope','motherboard_plane','front_psu_shared_cavity','radiator_translate_on_mount_plane','cooler_normal']
              }
            });
          }
        }
        continue;
      }

      // Normal reference path retains the V1.7.2 authority order: cooling is planned from
      // the fixed board/GPU structure and PSU remains the final residual consumer.
      const radReservations=planningRadiatorPlacements(bounds,inventory,'reference',structuralCore,effective);
      const coolingBranches=radReservations.length?radReservations:[null];
      for(const radReservation of coolingBranches){
        const actualCoolingCore=[
          {desc:inventory.mobo,placement:mobo},
          boardKeepout,
          {desc:inventory.gpu,placement:gpu},
          ...(inventory.cooler?.actual?[{desc:inventory.cooler,placement:cooler}]:[])
        ];
        const radActual=materializeRadiatorFromReservation(bounds,inventory,radReservation,'reference',actualCoolingCore,effective);
        const activeAssembly=(radActual.placement&&radActual.assembly?.length)?radActual.assembly:assemblyOccupied(radReservation);

        const occupiedForPsu=[
          {desc:inventory.mobo,placement:mobo},
          boardKeepout,
          {desc:inventory.reserve.gpu,placement:gpuReserve},
          ...(!liquidBranch?[{desc:inventory.reserve.cooler,placement:coolerReserve}]:[]),
          ...activeAssembly
        ];
        const primaryRegion=referencePsuRegion(bounds,inventory,mobo,gpuReserve,effective);
        let psus=psuCandidates(bounds,inventory.psu,'reference',occupiedForPsu,primaryRegion,effective);
        if(!psus.length){
          const fullReferenceRegion={min:[left,floor,rear],max:[right,ceiling,front]};
          psus=psuCandidates(bounds,inventory.psu,'reference',occupiedForPsu,fullReferenceRegion,effective);
        }
        const psuChoices=psus.length?psus.slice(0,4):[null];

        for(const psu of psuChoices){
          arrangements.push({
            bounds,mobo,gpu,cooler,psu,psuCandidates:psus,radiator:radActual.placement,
            radiatorCandidates:radActual.candidates,spineX:null,
            configurationBranch:inventory.configurationBranch,
            reservations:{
              gpu:gpuReserve,
              cpu:coolerReserve,
              motherboardKeepout:boardKeepout.placement,
              psuPlatform:null,
              radiator:radReservation?.placement||null,
              radiatorAssembly:activeAssembly,
              structuralRadiatorAssembly:radReservation?.assembly||[],
              solveOrder:['gpu_capability_envelope','motherboard_plane','cooling_cavity','psu_final_topology_region','cooler_normal']
            }
          });
        }
      }
    }
  }
  return arrangements;
}

function oppositeChamber(side){return side==='left'?'right':'left';}

function sandwichMountProfile(effective) {
  const hints=effective?.spatialHints||{};
  let motherboardSide=normalizedMotherboardSide(
    hints.motherboardChamber
    || hints.motherboardSide
    || hints.boardChamber
    || hints.boardSide
  );
  let gpuSide=normalizedMotherboardSide(hints.gpuChamber||hints.gpuSide);
  let source='';

  const handedness=String(hints.sandwichHandedness||hints.chamberHandedness||'').trim().toLowerCase().replace(/[_-]+/g,' ');
  if(!motherboardSide && handedness){
    if(/(?:motherboard|mobo|board)\s+left|left\s+(?:motherboard|mobo|board)/.test(handedness))motherboardSide='left';
    else if(/(?:motherboard|mobo|board)\s+right|right\s+(?:motherboard|mobo|board)/.test(handedness))motherboardSide='right';
  }
  if(!gpuSide && handedness){
    if(/gpu\s+left|left\s+gpu/.test(handedness))gpuSide='left';
    else if(/gpu\s+right|right\s+gpu/.test(handedness))gpuSide='right';
  }
  if(motherboardSide||gpuSide)source='structured_chamber_semantics';

  if(!motherboardSide && gpuSide)motherboardSide=oppositeChamber(gpuSide);
  if(!gpuSide && motherboardSide)gpuSide=oppositeChamber(motherboardSide);

  if(!motherboardSide){
    const modeText=String(effective?.modeTraits?.modeRaw||effective?.activeMode?.mode_name||effective?.activeMode?.name||'').toLowerCase();
    // Most conventional/slot-indexed sandwich frames expose motherboard on the LEFT and GPU
    // on the RIGHT. Swing-arm records phrased as Max CPU / Max GPU are the one generic legacy
    // profile in the current dataset that uses the opposite physical handedness (Terra-style).
    // This is a migration fallback only; explicit motherboardChamber/gpuChamber data wins.
    const swingArmMode=/\bmax(?:imum)?\s*(?:cpu|gpu)\b/.test(modeText);
    motherboardSide=swingArmMode?'right':'left';
    gpuSide=oppositeChamber(motherboardSide);
    source=swingArmMode?'mode_mount_profile_fallback':'sandwich_canonical_fallback';
  }

  if(hints.reverse===true || effective?.modeTraits?.flipped===true){
    motherboardSide=oppositeChamber(motherboardSide);
    gpuSide=oppositeChamber(gpuSide);
    source=source?`${source}+explicit_reverse`:'explicit_reverse';
  }

  return {motherboardSide,gpuSide,source,mirrored:motherboardSide==='right'};
}

function sandwichMountProfiles(effective){
  // One active mode => one chamber handedness. `allowMirror` is retained as capability
  // metadata for UI/manual controls but is deliberately not a ranked deterministic branch.
  // Otherwise PSU/cooling score can make the complete motherboard appear to flip by case.
  return [{...sandwichMountProfile(effective),priority:100}];
}

function sandwichAllocation(h, inventory, effective) {
  // Tall/vertical sandwich cases often use nearly the full front-to-back motherboard plane.
  // A blanket 3 mm shell inset made a 244 mm mATX board fail inside a 247 mm-deep Meshroom
  // by exactly 3 mm. Preserve the normal safety inset for ordinary sandwich cases, but use
  // a 1 mm geometric shell for verified tall sandwich topology; published capability rules
  // remain the authoritative compatibility limits.
  const canonical=h.canonical || {};
  const tallVertical=isUprightSandwich(effective);
  const bounds=interiorBounds(canonical,tallVertical?1:3);
  const innerWidth=bounds.max[0]-bounds.min[0];
  const limits=effective.limits||{};
  // Chamber allocation is derived from published capability, not the currently selected
  // cooler/GPU. Selection therefore cannot flip the topology later.
  let gpuAlloc=finite(limits.maxGPUT,0)||Math.max(finite(inventory.reserve?.gpu?.dims?.w,50)+4,innerWidth*0.42);
  let cpuAlloc=finite(limits.maxCPU,0)||Math.max(finite(inventory.reserve?.cooler?.dims?.h,47)+4,innerWidth-gpuAlloc-2);
  const targetSum=Math.max(1,gpuAlloc+cpuAlloc);
  const available=Math.max(20,innerWidth-2);
  if(targetSum>available){
    const ratio=clamp(gpuAlloc/targetSum,0.1,0.9);
    gpuAlloc=available*ratio;
    cpuAlloc=available-gpuAlloc;
  }else if(targetSum<available){
    const ratio=clamp(gpuAlloc/targetSum,0.1,0.9);
    gpuAlloc=available*ratio;
    cpuAlloc=available-gpuAlloc;
  }
  return {bounds,innerWidth,gpuAlloc,cpuAlloc,source:'capability_envelope'};
}

function sandwichLikeArrangements(h,inventory,effective,family){
  const alloc=sandwichAllocation(h,inventory,effective);
  const {bounds}=alloc;
  const rear=bounds.min[2], ceiling=bounds.max[1], left=bounds.min[0], right=bounds.max[0];
  const verticalSandwich=isUprightSandwich(effective);
  const arrangements=[];

  for(const mountProfile of sandwichMountProfiles(effective)){
    const motherboardSide=mountProfile.motherboardSide;
    const gpuSide=mountProfile.gpuSide;
    const spineX=gpuSide==='right' ? right-alloc.gpuAlloc : left+alloc.gpuAlloc;

    // Component side faces its outer chamber panel. Rear I/O remains the physical anchor;
    // with the chiral board frame this automatically yields PCIe DOWN for motherboard-left
    // (class 23) and PCIe UP for motherboard-right (class 19). No CPU/RAM/PCIe landmark is
    // independently swapped per case.
    const componentNormalX=motherboardSide==='left'?-1:1;
    const explicitIoEdge=effective?.spatialHints?.motherboardIoEdge
      ?? effective?.spatialHints?.boardIoEdge
      ?? effective?.spatialHints?.ioEdge;
    const explicitPcieEdge=effective?.spatialHints?.motherboardPcieEdge
      ?? effective?.spatialHints?.boardPcieEdge;
    const moboOrientation=explicitPcieEdge!=null
      ? motherboardOrientationForPcieEdge(componentNormalX,explicitPcieEdge)
      : motherboardOrientationClass(componentNormalX,explicitIoEdge??'rear');
    const frameSemantics=motherboardOrientationSemantics(moboOrientation)||{};
    const moboSize=orientedSize(componentLocalSize('motherboard',inventory.mobo.dims),moboOrientation);
    const boardNormalSize=moboSize[0];
    const moboX=motherboardSide==='left'
      ? spineX-boardNormalSize/2
      : spineX+boardNormalSize/2;

    // GPU occupies the opposite chamber. In a physically upright sandwich only the GPU
    // relationship becomes vertical; this is not a B4-style whole-reference transform.
    const gpuOrientation=verticalSandwich
      ? (gpuSide==='right'?5:13)
      : (gpuSide==='right'?12:6);
    const gpuSize=orientedSize(componentLocalSize('gpu',inventory.gpu.dims),gpuOrientation);
    const gpuReserveSize=orientedSize(componentLocalSize('gpu',inventory.reserve.gpu.dims),gpuOrientation);
    const gpuPsuReserveDesc=inventory.gpu?.actual ? {
      ...inventory.reserve.gpu,
      dims:{...inventory.reserve.gpu.dims,w:finite(inventory.gpu?.dims?.w,inventory.reserve.gpu.dims.w)},
      actual:false,structuralReservation:true,psuRelationalEnvelope:true
    } : inventory.reserve.gpu;
    const gpuPsuReserveSize=orientedSize(componentLocalSize('gpu',gpuPsuReserveDesc.dims),gpuOrientation);
    const gpuX=gpuSide==='right'?right-gpuSize[0]/2:left+gpuSize[0]/2;
    const reserveGpuX=gpuSide==='right'?right-gpuReserveSize[0]/2:left+gpuReserveSize[0]/2;
    const psuReserveGpuX=gpuSide==='right'?right-gpuPsuReserveSize[0]/2:left+gpuPsuReserveSize[0]/2;

    // Cooling is planned before final MB/GPU Y. For vertical sandwich this becomes an upper
    // side/end plane rather than a global chassis rotation. The generic max radiator is tried
    // first; if it cannot be represented but a selected catalog AIO can, that real body is
    // allowed to define the planning cavity.
    const radReservations=planningRadiatorPlacements(bounds,inventory,family,[],effective);
    const coolingBranches=radReservations.length?radReservations:[null];
    for(const radReservation of coolingBranches){
      const moboFootprint={
        min:[moboX-moboSize[0]/2,bounds.min[1],rear],
        max:[moboX+moboSize[0]/2,bounds.max[1],rear+moboSize[2]]
      };
      const gpuFootprint={
        min:[gpuX-gpuSize[0]/2,bounds.min[1],rear],
        max:[gpuX+gpuSize[0]/2,bounds.max[1],rear+gpuSize[2]]
      };
      const reserveGpuFootprint={
        min:[reserveGpuX-gpuReserveSize[0]/2,bounds.min[1],rear],
        max:[reserveGpuX+gpuReserveSize[0]/2,bounds.max[1],rear+gpuReserveSize[2]]
      };
      const moboUpper=ceilingBelowTopAssembly(bounds,moboFootprint,radReservation,0.5);
      const gpuUpper=ceilingBelowTopAssembly(bounds,gpuFootprint,radReservation,0.5);
      const reserveGpuUpper=ceilingBelowTopAssembly(bounds,reserveGpuFootprint,radReservation,0.5);
      const moboY=topAlignedPlacementY(bounds,moboSize,moboUpper);
      const gpuY=topAlignedPlacementY(bounds,gpuSize,gpuUpper);
      const reserveGpuY=topAlignedPlacementY(bounds,gpuReserveSize,reserveGpuUpper);
      const psuReserveGpuY=topAlignedPlacementY(bounds,gpuPsuReserveSize,reserveGpuUpper);

      const mountKey=`mobo_${motherboardSide}_gpu_${gpuSide}`;
      const mobo=candidate('motherboard',`${family}_${mountKey}`,[moboX,moboY,rear+moboSize[2]/2],moboOrientation,`spine_motherboard_${motherboardSide}`,mountProfile.priority,{structuralCeiling:moboUpper,verticalSandwich,motherboardFrameAuthority:'chiral_rigid_board_frame_v3',componentNormalX,ioEdge:frameSemantics.ioEdge||'rear',pcieEdge:frameSemantics.pcieEdge||null,motherboardChamber:motherboardSide,gpuChamber:gpuSide,chamberSource:mountProfile.source,frameSource:explicitPcieEdge!=null?'structured_pcie_edge':(explicitIoEdge!=null?'structured_io_edge':'rear_io_mount_semantics')});
      const cooler=coolerPlacement(inventory,mobo);
      const coolerReserve=coolerReservePlacement(inventory,mobo);
      const gpu=candidate('gpu',`${family}_gpu_${gpuSide}`,[gpuX,gpuY,rear+gpuSize[2]/2],gpuOrientation,`opposite_panel_${gpuSide}`,mountProfile.priority,{structuralCeiling:gpuUpper,verticalSandwich,motherboardChamber:motherboardSide,gpuChamber:gpuSide,chamberSource:mountProfile.source});
      const gpuReserve=candidate('gpu',`${family}_gpu_reserved_${gpuSide}`,[reserveGpuX,reserveGpuY,rear+gpuReserveSize[2]/2],gpuOrientation,'gpu_capability_reserved',120,{structuralReservation:true,verticalSandwich,motherboardChamber:motherboardSide,gpuChamber:gpuSide,chamberSource:mountProfile.source});
      const gpuPsuReserve=candidate('gpu',`${family}_gpu_psu_envelope_${gpuSide}`,[psuReserveGpuX,psuReserveGpuY,rear+gpuPsuReserveSize[2]/2],gpuOrientation,'gpu_psu_relational_envelope',120,{structuralReservation:true,verticalSandwich,selectedThickness:true,motherboardChamber:motherboardSide,gpuChamber:gpuSide,chamberSource:mountProfile.source});

      // Materialise the selected catalog AIO BEFORE PSU placement so real radiator + fan
      // dimensions own their space. PSU is a late residual-cavity solve.
      const actualCoolingCore=[
        {desc:inventory.mobo,placement:mobo},
        {desc:inventory.gpu,placement:gpu},
        {desc:inventory.cooler,placement:cooler}
      ];
      const radActual=materializeRadiatorFromReservation(bounds,inventory,radReservation,family,actualCoolingCore,effective);
      const activeAssembly=(radActual.placement&&radActual.assembly?.length)?radActual.assembly:assemblyOccupied(radReservation);
      const coolingForPsu=inventory.radiator?.actual ? activeAssembly : [];
      const psuCore=[
        {desc:inventory.mobo,placement:mobo},
        {desc:gpuPsuReserveDesc,placement:gpuPsuReserve},
        ...(inventory.cooler?.actual?[{desc:inventory.cooler,placement:cooler}]:[]),
        ...coolingForPsu
      ];
      const psus=psuCandidates(bounds,inventory.psu,family,psuCore,null,effective);
      const psuChoices=psus.length?psus.slice(0,4):[null];
      for(const psu of psuChoices){
        arrangements.push({
          bounds,mobo,gpu,cooler,psu,psuCandidates:psus,radiator:radActual.placement,
          radiatorCandidates:radActual.candidates,spineX,allocation:{...alloc,spineX,motherboardChamber:motherboardSide,gpuChamber:gpuSide,chamberSource:mountProfile.source},
          configurationBranch:inventory.configurationBranch,
          reservations:{
            gpu:gpuReserve,
            cpu:coolerReserve,
            radiator:radReservation?.placement||null,
            radiatorAssembly:activeAssembly,
            structuralRadiatorAssembly:radReservation?.assembly||[],
            topDepth:radReservation?coolingAssemblyDepth(effective,radReservation.desc||inventory.reserve.radiator):0,
            solveOrder:verticalSandwich
              ? ['vertical_chamber_envelope','upper_cooling_assembly','component_local_ceiling','psu_final_residual']
              : ['chamber_envelope','cooling_assembly','component_local_ceiling','psu_final_residual']
          }
        });
      }
    }
  }
  return arrangements;
}

function consoleArrangements(h, inventory, effective) {
  const bounds=interiorBounds(h.canonical,3);
  const rear=bounds.min[2], floor=bounds.min[1], ceiling=bounds.max[1], left=bounds.min[0], right=bounds.max[0];
  const arrangements=[];
  // Mirror capability is not a scoring branch. Only an explicitly active reverse/flipped
  // state changes the panel relationship.
  const reverse=effective?.spatialHints?.reverse===true || effective?.modeTraits?.flipped===true;

  // Console is its own panel topology: motherboard and GPU occupy parallel broad panel
  // planes, the CPU cooler inherits the motherboard normal, and PSU orientation may become
  // upright only when the thin residual cavity makes that physically necessary.
  for (const reverseState of [reverse]) {
    // Canonical console handedness: motherboard LEFT, GPU RIGHT. Mirror/reverse is only
    // unlocked by explicit mode/spatial guidance instead of being the default.
    const boardSide = reverseState ? right : left;
    const gpuSide = reverseState ? left : right;
    // Console keeps the component side facing inward from the chosen panel, but rear I/O is
    // the physical orientation anchor. On the canonical LEFT panel (+X component normal),
    // rear I/O requires class 19 and therefore the PCIe edge is at the TOP. This matches
    // Ridge-style console packaging without relocating any board landmark independently.
    const componentNormalX = reverseState ? -1 : 1;
    const explicitIoEdge=effective?.spatialHints?.motherboardIoEdge
      ?? effective?.spatialHints?.boardIoEdge
      ?? effective?.spatialHints?.ioEdge;
    const explicitPcieEdge=effective?.spatialHints?.motherboardPcieEdge
      ?? effective?.spatialHints?.boardPcieEdge;
    const moboOrientation=explicitPcieEdge!=null
      ? motherboardOrientationForPcieEdge(componentNormalX,explicitPcieEdge)
      : motherboardOrientationClass(componentNormalX,explicitIoEdge??'rear');
    const frameSemantics=motherboardOrientationSemantics(moboOrientation)||{};
    const moboSize = orientedSize(componentLocalSize('motherboard',inventory.mobo.dims),moboOrientation);
    const moboX = reverseState ? boardSide - moboSize[0]/2 : boardSide + moboSize[0]/2;
    const moboY = floor + moboSize[1]/2;
    const moboZ = rear + moboSize[2]/2;
    const mobo=candidate('motherboard',`console_mobo_${reverseState?'right':'left'}`,[moboX,moboY,moboZ],moboOrientation,`panel_${reverseState?'right':'left'}`,100,{panelTopology:true,motherboardFrameAuthority:'chiral_rigid_board_frame_v3',componentNormalX,ioEdge:frameSemantics.ioEdge||'rear',pcieEdge:frameSemantics.pcieEdge||null,motherboardChamber:reverseState?'right':'left',frameSource:explicitPcieEdge!=null?'structured_pcie_edge':(explicitIoEdge!=null?'structured_io_edge':'console_rear_io')});

    const gpuOrientation = reverseState ? 6 : 12;
    const gpuSize = orientedSize(componentLocalSize('gpu',inventory.gpu.dims),gpuOrientation);
    const gpuReserveSize = orientedSize(componentLocalSize('gpu',inventory.reserve.gpu.dims),gpuOrientation);
    const gpuX = reverseState ? gpuSide+gpuSize[0]/2 : gpuSide-gpuSize[0]/2;
    const reserveGpuX = reverseState ? gpuSide+gpuReserveSize[0]/2 : gpuSide-gpuReserveSize[0]/2;
    const gpuY = ceiling-gpuSize[1]/2;
    const gpuReserveY = ceiling-gpuReserveSize[1]/2;
    const gpuZ = rear+gpuSize[2]/2;
    const gpuReserveZ = rear+gpuReserveSize[2]/2;
    const gpu=candidate('gpu',`console_gpu_${reverseState?'left':'right'}`,[gpuX,gpuY,gpuZ],gpuOrientation,`panel_${reverseState?'left':'right'}`,100,{panelTopology:true});
    const gpuReserve=candidate('gpu',`console_gpu_reserved_${reverseState?'left':'right'}`,[reserveGpuX,gpuReserveY,gpuReserveZ],gpuOrientation,'console_gpu_capability_reserved',120,{structuralReservation:true,panelTopology:true});

    for(const quarterTurn of [false,true]){
      const cooler=coolerPlacement(inventory,mobo,quarterTurn);
      const coolerReserve=coolerReservePlacement(inventory,mobo,quarterTurn);
      const structuralCore=[
        {desc:inventory.mobo,placement:mobo},
        {desc:inventory.reserve.gpu,placement:gpuReserve},
        {desc:inventory.reserve.cooler,placement:coolerReserve}
      ];
      const radReservations=planningRadiatorPlacements(bounds,inventory,'console',structuralCore,effective);
      const coolingBranches=radReservations.length?radReservations:[null];

      for(const radReservation of coolingBranches){
        const actualCoolingCore=[{desc:inventory.mobo,placement:mobo},{desc:inventory.gpu,placement:gpu},{desc:inventory.cooler,placement:cooler}];
        const radActual=materializeRadiatorFromReservation(bounds,inventory,radReservation,'console',actualCoolingCore,effective);
        const activeAssembly=(radActual.placement&&radActual.assembly?.length)?radActual.assembly:assemblyOccupied(radReservation);
        const lowerRegion={
          min:[bounds.min[0],bounds.min[1],bounds.min[2]],
          max:[bounds.max[0],Math.min(bounds.max[1],floor+Math.max(125,(bounds.max[1]-bounds.min[1])*0.52)),bounds.max[2]]
        };
        const occupiedForPsu=[...structuralCore,...activeAssembly];
        let psus=psuCandidates(bounds,inventory.psu,'console',occupiedForPsu,lowerRegion,effective);
        if(!psus.length)psus=psuCandidates(bounds,inventory.psu,'console',occupiedForPsu,null,effective);
        const psuChoices=psus.length?psus.slice(0,4):[null];

        for(const psu of psuChoices){
          arrangements.push({
            bounds,mobo,gpu,cooler,psu,psuCandidates:psus,radiator:radActual.placement,
            radiatorCandidates:radActual.candidates,spineX:null,
            configurationBranch:inventory.configurationBranch,
            reservations:{gpu:gpuReserve,cpu:coolerReserve,radiator:radReservation?.placement||null,radiatorAssembly:activeAssembly,structuralRadiatorAssembly:radReservation?.assembly||[],solveOrder:['panel_pair','cooler_normal','cooling_reservation','psu_final_residual']}
          });
        }
      }
    }
  }
  return arrangements;
}

function scoreCapabilityPool(h, capabilityProfile, bounds, effective={}) {
  if (!capabilityProfile) return { score:0, penalty:0, reasons:[] };
  const innerW=bounds.max[0]-bounds.min[0], innerH=bounds.max[1]-bounds.min[1], innerL=bounds.max[2]-bounds.min[2];
  const verticalSandwich=h.family==='sandwich' && isUprightSandwich(effective);
  const gpuLengthAxis=verticalSandwich?innerH:innerL;
  let score=0, penalty=0;
  const reasons=[];

  // Each capability state is an alternative supported configuration, not a simultaneous
  // build. Score how well this topology explains the whole set of published envelopes.
  const states=capabilityProfile.states || [];
  if (states.length) {
    let explained=0;
    for (const state of states) {
      const lim=state.limits || {};
      let ok=true;
      if (finite(lim.maxGPU,0)>0 && lim.maxGPU>gpuLengthAxis+8) ok=false;
      if (h.family==='reference') {
        if (finite(lim.maxCPU,0)>0 && lim.maxCPU>innerW+8) ok=false;
        if (finite(lim.maxGPUH,0)>0 && lim.maxGPUH>innerW+12) ok=false;
      } else if (h.family==='sandwich') {
        if (finite(lim.maxGPUT,0)>0 && finite(lim.maxCPU,0)>0 && (lim.maxGPUT+lim.maxCPU)>innerW+12) ok=false;
        if (finite(lim.maxGPUH,0)>0 && lim.maxGPUH>(verticalSandwich?innerL:innerH)+12) ok=false;
      } else if (h.family==='console') {
        // Console layouts use the thin axis for board/GPU thickness and large panel axes
        // for GPU height/length. Do not apply the sandwich CPU+GPU spine sum here.
        if (finite(lim.maxGPUT,0)>0 && lim.maxGPUT>innerW+8) ok=false;
        if (finite(lim.maxGPUH,0)>0 && lim.maxGPUH>innerH+12 && lim.maxGPUH>innerL+12) ok=false;
      }
      if(ok) explained+=1;
    }
    const coverage=explained/states.length;
    score += coverage*24;
    penalty += (1-coverage)*18;
    if(coverage>=0.8) reasons.push('capability_pool_explained');
    else reasons.push(`capability_pool_coverage:${coverage.toFixed(2)}`);
  }

  const boards=capabilityProfile.motherboardEnvelopes || [];
  if (boards.length) {
    const boardFit=boards.filter(b => {
      const dims=b.dims||{};
      // board plane occupies canonical Y/Z; either board side may be selected.
      return finite(dims.w,0)<=innerH+8 && finite(dims.l,0)<=innerL+8;
    }).length/boards.length;
    score += boardFit*10;
    penalty += (1-boardFit)*12;
  }

  // Relationship rules tell us which component classes share contested space. Reward
  // families that make those relationships structurally plausible.
  for (const hint of capabilityProfile.relationHints || []) {
    const c=new Set(hint.conditionKeys||[]), o=new Set(hint.overrideKeys||[]);
    if ((c.has('psu')||c.has('mobo')) && o.has('maxAIO')) {
      if (h.family==='reference') score+=3;
      reasons.push('psu_mobo_aio_shared_cavity');
    }
    if ([...c].some(k=>k.startsWith('gpu')) && (o.has('maxGPUT')||o.has('maxGPU'))) {
      score+=2; reasons.push('gpu_shape_constraint_present');
    }
  }

  return {score,penalty,reasons};
}

function scoreCapability(h,inventory,effective,arrangement,capabilityProfile=null){
  const bounds=arrangement.bounds;
  const limits=effective.limits||{};
  let score=0, hardPenalty=0;
  const reasons=[];
  const branch=inventory.configurationBranch || effective?.configurationBranch || null;
  if(branch){
    score += Math.max(-20,Math.min(20,(finite(branch.priority,100)-100)*0.08));
    if(branch.valid===false){hardPenalty+=180;reasons.push('configuration_branch_invalid');}
    if(Array.isArray(branch.warnings)&&branch.warnings.length)reasons.push('configuration_branch_warning');
  }
  const placements=[
    [inventory.mobo,arrangement.mobo],
    [inventory.gpu,arrangement.gpu],
    [inventory.cooler,arrangement.cooler],
    arrangement.psu?[inventory.psu,arrangement.psu]:null,
    arrangement.radiator&&inventory.radiator?[inventory.radiator,arrangement.radiator]:null
  ].filter(Boolean);

  for(const [desc,p] of placements){
    const overflow=overflowDistance(bounds,placementBox(desc,p).bounds);
    if(overflow<=1.5)score+=desc.actual?12:5;
    else{
      const scale=desc.actual?3.5:1.2;
      hardPenalty+=overflow*scale;
      reasons.push(`${desc.type}_overflow:${overflow.toFixed(1)}`);
    }
  }

  if(arrangement.psu){
    const c=collisionPenalty({desc:inventory.psu,placement:arrangement.psu},[
      {desc:inventory.mobo,placement:arrangement.mobo},
      {desc:inventory.gpu,placement:arrangement.gpu},
      {desc:inventory.cooler,placement:arrangement.cooler}
    ]);
    hardPenalty+=c;
    if(c<2)score+=10;
    const largeBoardSmallPsu=h.family==='reference'
      && ['MATX','ATX'].includes(String(inventory?.mobo?.formFactor||'').toUpperCase())
      && ['SFX','SFX-L'].includes(String(inventory?.psu?.formFactor||'').toUpperCase());
    if(largeBoardSmallPsu){
      // This is a facing/region preference, not a new PSU axis. It makes the solver prefer
      // the front-facing SFX arrangement when a large motherboard claims the normal cavity.
      if(arrangement.psu.mountFace==='front')score+=12;
      if(PSU_FRONT_FACING_ORIENTATIONS.includes(arrangement.psu.orientationClass))score+=6;
    }
  }else{hardPenalty+=inventory.psu.actual?80:22;reasons.push('no_psu_candidate');}

  if(arrangement.reservations?.radiator) score+=8;
  if(branch?.cooling==='liquid' && inventory.radiator?.actual){
    if(arrangement.radiator)score+=14;
    else{hardPenalty+=120;reasons.push('no_cooling_assembly_mount');}
  }
  if(arrangement.mobo?.structuralFit===false){
    hardPenalty+=Math.max(40,finite(arrangement.mobo.structuralOverflow,0)*8);
    reasons.push('motherboard_structural_region_overflow');
  }

  const innerW=bounds.max[0]-bounds.min[0],innerH=bounds.max[1]-bounds.min[1],innerL=bounds.max[2]-bounds.min[2];
  const maxGpu=finite(limits.maxGPU,0),maxGpuH=finite(limits.maxGPUH,0),maxGpuT=finite(limits.maxGPUT,0),maxCpu=finite(limits.maxCPU,0);
  const verticalSandwich=h.family==='sandwich' && isUprightSandwich(effective);
  const gpuLengthAxis=verticalSandwich?innerH:innerL;
  if(maxGpu>0){const d=maxGpu-gpuLengthAxis;if(d<=5)score+=9;else{hardPenalty+=(d-5)*1.5;reasons.push('published_gpu_axis_mismatch');}}

  if(h.family==='reference'){
    if(maxGpuH>0){const d=maxGpuH-innerW;if(d<=6)score+=5;else hardPenalty+=(d-6)*1.2;}
    if(maxCpu>0){const d=maxCpu-innerW;if(d<=6)score+=7;else hardPenalty+=(d-6)*1.5;}
  }else{
    if(maxGpuH>0){const d=maxGpuH-(verticalSandwich?innerL:innerH);if(d<=6)score+=5;else hardPenalty+=(d-6)*1.2;}
    const actualPair=finite(inventory.gpu.dims.w,0)+finite(inventory.cooler.dims.h,0)+2;
    if(inventory.gpu.actual&&inventory.cooler.actual){
      if(actualPair<=innerW+4)score+=14;else{hardPenalty+=(actualPair-innerW)*2.5;reasons.push('active_chamber_width_conflict');}
    }else if(effective.fixedStep&&maxGpuT>0&&maxCpu>0){
      const d=(maxGpuT+maxCpu+2)-innerW;
      if(d<=7)score+=14;else hardPenalty+=(d-7)*1.8;
    }
  }

  // Actual component cross-collisions are meaningful. Virtual maxima often describe
  // separate configurations, so their penalties are intentionally reduced.
  const actualOccupied=placements.map(([desc,placement])=>({desc,placement}));
  for(let i=0;i<actualOccupied.length;i+=1){
    for(let j=i+1;j<actualOccupied.length;j+=1){
      const a=actualOccupied[i],b=actualOccupied[j];
      const pair=new Set([a.desc.type,b.desc.type]);
      if(pair.has('motherboard')&&pair.has('gpu'))continue;
      if(pair.has('motherboard')&&pair.has('aircooler'))continue;
      const ratio=overlapRatio(placementBox(a.desc,a.placement).bounds,placementBox(b.desc,b.placement).bounds);
      if(!ratio)continue;
      const weight=a.desc.actual&&b.desc.actual?120:18;
      hardPenalty+=ratio*weight;
    }
  }

  const poolScore=scoreCapabilityPool(h,capabilityProfile,bounds,effective);
  score+=poolScore.score;
  hardPenalty+=poolScore.penalty;
  reasons.push(...poolScore.reasons);

  score-=hardPenalty;
  return {score,hardPenalty,reasons};
}

function arrangementMountVariants(arrangement, maxPsuVariants=3) {
  const psus = Array.isArray(arrangement?.psuCandidates) && arrangement.psuCandidates.length
    ? arrangement.psuCandidates.slice(0,maxPsuVariants)
    : [arrangement?.psu || null];
  const out=[];
  for (const psu of psus) out.push({ ...arrangement, psu });
  return out;
}

function solveHypothesisCandidates(h, inventory, effective, capabilityProfile=null){
  const arrangements=h.family==='reference'
    ? referenceArrangements(h,inventory,effective)
    : h.family==='console'
      ? consoleArrangements(h,inventory,effective)
      : sandwichLikeArrangements(h,inventory,effective,'sandwich');
  const expanded=arrangements.flatMap(arrangement=>arrangementMountVariants(arrangement));
  return expanded.map(arrangement=>{
    const fit=scoreCapability(h,inventory,effective,arrangement,capabilityProfile);
    return {...h,arrangement,score:h.priorScore+fit.score,fit,prior:h.priorScore};
  }).sort((a,b)=>b.score-a.score);
}

function solveHypothesis(h, inventory, effective, capabilityProfile=null){
  return solveHypothesisCandidates(h,inventory,effective,capabilityProfile)[0] || null;
}

function createComponentValidity(inventory) {
  const out = {};
  for (const desc of [inventory?.mobo, inventory?.gpu, inventory?.psu, inventory?.cooler, inventory?.radiator]) {
    if (!desc?.type || !desc.actual) continue;
    out[desc.type] = { valid:true, reasons:[] };
  }
  return out;
}

function invalidateComponent(components, type, reason) {
  if (!type) return;
  if (!components[type]) components[type] = { valid:true, reasons:[] };
  components[type].valid = false;
  if (reason && !components[type].reasons.includes(reason)) components[type].reasons.push(reason);
}

function validatePublishedLimits(inventory, effective, components, reasons, capabilityProfile=null) {
  const limits = effective?.validationLimits || effective?.limits || {};
  const mobo=inventory?.mobo;
  if(mobo?.actual && capabilityProfile?.motherboardSupportKnown){
    const supported=capabilityProfile.motherboardTypes || [];
    if(mobo.formFactor && !supported.includes(mobo.formFactor)){
      const detail=`motherboard_form_factor_unsupported:${mobo.formFactor}`;
      reasons.push(detail); invalidateComponent(components,'motherboard',detail);
    }
  }
  const psu=inventory?.psu;
  if(psu?.actual && capabilityProfile?.psuSupportKnown){
    const supported=capabilityProfile.psuTypes || [];
    if(psu.formFactor && !supported.includes(psu.formFactor)){
      const detail=`psu_form_factor_unsupported:${psu.formFactor}`;
      reasons.push(detail); invalidateComponent(components,'psu',detail);
    }
  }
  const gpu = inventory?.gpu;
  if (gpu?.actual) {
    const checks = [
      ['maxGPU', finite(gpu.dims?.l,0), finite(limits.maxGPU,0), 'gpu_length_limit', true],
      ['maxGPUT', finite(gpu.dims?.w,0), finite(limits.maxGPUT,0), 'gpu_thickness_limit', true],
      // Missing total-height data uses a render/spatial proxy. Never turn that proxy into
      // deterministic physical compatibility authority.
      ['maxGPUH', finite(gpu.dims?.h,0), finite(limits.maxGPUH,0), 'gpu_height_limit', true]
    ];
    for (const [,actual,limit,reason,known] of checks) {
      if (known && limit > 0 && actual > limit + 0.01) {
        const detail = `${reason}:${actual.toFixed(1)}>${limit.toFixed(1)}`;
        reasons.push(detail);
        invalidateComponent(components,'gpu',detail);
      }
    }
  }
  const cooler = inventory?.cooler;
  if (cooler?.actual) {
    const actual = finite(cooler.dims?.h,0), limit = finite(limits.maxCPU,0);
    if (limit > 0 && actual > limit + 0.01) {
      const detail = `cpu_cooler_height_limit:${actual.toFixed(1)}>${limit.toFixed(1)}`;
      reasons.push(detail);
      invalidateComponent(components,'aircooler',detail);
    }
  }
  const radiator = inventory?.radiator;
  if (radiator?.actual) {
    const actual = finite(radiator.radiatorSize,0), limit = finite(limits.maxAIO,0);
    if (actual > 0 && limit > 0 && actual > limit + 0.01) {
      const detail = `radiator_size_limit:${actual.toFixed(0)}>${limit.toFixed(0)}`;
      reasons.push(detail);
      invalidateComponent(components,'radiator',detail);
    }
  }
}

function hardArrangementValidation(solution, inventory, effective, capabilityProfile=null) {
  const a=solution?.arrangement;
  const components=createComponentValidity(inventory);
  if(!a?.bounds) return {valid:false,reasons:['missing_bounds'],overflow:999,collision:999,components};
  const bounds=a.bounds;
  const placements=[
    [inventory.mobo,a.mobo],
    [inventory.gpu,a.gpu],
    [inventory.cooler,a.cooler],
    [inventory.psu,a.psu],
    [inventory.radiator,a.radiator]
  ].filter(([desc])=>!!desc);
  let totalOverflow=0,totalCollision=0;
  const reasons=[];
  const branch=inventory.configurationBranch || effective?.configurationBranch || null;
  if(branch?.valid===false){
    for(const failure of branch.failures || ['configuration_branch_invalid']) reasons.push(`configuration_rule:${failure}`);
    const details=Array.isArray(branch?.failureDetails)?branch.failureDetails:[];
    if(details.length){
      const ownerMap={motherboard:'motherboard',psu:'psu',gpu:'gpu',aircooler:'aircooler',radiator:'radiator'};
      for(const detail of details){
        const detailReason=`configuration_branch_invalid:${String(detail?.message||'rule')}`;
        for(const owner of detail?.owners||[]){
          const type=ownerMap[owner];
          if(type==='motherboard'&&inventory?.mobo?.actual)invalidateComponent(components,'motherboard',detailReason);
          else if(type==='psu'&&inventory?.psu?.actual)invalidateComponent(components,'psu',detailReason);
          else if(type==='gpu'&&inventory?.gpu?.actual)invalidateComponent(components,'gpu',detailReason);
          else if(type==='aircooler'&&inventory?.cooler?.actual)invalidateComponent(components,'aircooler',detailReason);
          else if(type==='radiator'&&inventory?.radiator?.actual)invalidateComponent(components,'radiator',detailReason);
        }
      }
    }else{
      // Legacy/unknown branch failures stay branch-wide. New branches carry owner metadata so
      // a PSU-only failure no longer falsely ejects a valid motherboard.
      if(inventory?.mobo?.actual)invalidateComponent(components,'motherboard','configuration_branch_invalid');
      if(inventory?.psu?.actual)invalidateComponent(components,'psu','configuration_branch_invalid');
    }
  }
  if(branch?.cooling==='liquid' && inventory?.radiator?.actual && !a.radiator){
    reasons.push('cooling_assembly_missing_mount');
    invalidateComponent(components,'radiator','cooling_assembly_missing_mount');
  }
  if(a.mobo?.structuralFit===false){
    reasons.push('motherboard_structural_region_overflow');
    if(inventory?.mobo?.actual)invalidateComponent(components,'motherboard','motherboard_structural_region_overflow');
  }

  validatePublishedLimits(inventory,effective,components,reasons,capabilityProfile);

  for(const [desc,p] of placements){
    if(!desc?.actual) continue;
    if(!p){
      const detail=`${desc.type}_missing_mount`;
      reasons.push(detail);
      invalidateComponent(components,desc.type,detail);
      continue;
    }
    const overflow=overflowDistance(bounds,placementBox(desc,p).bounds);
    totalOverflow+=overflow;
    if(overflow>2.0){
      const detail=`${desc.type}_outside:${overflow.toFixed(1)}`;
      reasons.push(detail);
      invalidateComponent(components,desc.type,detail);
    }
  }
  for(let i=0;i<placements.length;i+=1){
    for(let j=i+1;j<placements.length;j+=1){
      const [da,pa]=placements[i], [db,pb]=placements[j];
      if(!da?.actual||!db?.actual||!pa||!pb) continue;
      const pair=new Set([da.type,db.type]);
      // CPU cooler is socket-attached to the board. Reference GPU is allowed to meet the
      // PCIe edge/plane, but sandwich/console GPU and motherboard must remain separated.
      if(pair.has('motherboard')&&pair.has('aircooler')) continue;
      if(pair.has('motherboard')&&pair.has('gpu') && solution.family==='reference') continue;
      const ratio=overlapRatio(placementBox(da,pa).bounds,placementBox(db,pb).bounds);
      if(ratio<=0) continue;
      totalCollision+=ratio;
      if(ratio>0.015){
        const detail=`${da.type}_${db.type}_collision:${ratio.toFixed(3)}`;
        reasons.push(detail);
        invalidateComponent(components,da.type,detail);
        invalidateComponent(components,db.type,detail);
      }
    }
  }
  // Fan reservations guide structural planning even when the current build is air cooled,
  // but only the SELECTED AIO fan stack is deterministic occupancy for this build. Treating
  // an unselected max-AIO reservation as real hardware would incorrectly make perfectly
  // valid air-cooled branches fail when published maxima are mutually conditional.
  if(inventory?.radiator?.actual){
    const fanAssembly=(a.reservations?.radiatorAssembly || []).filter(item=>item?.desc?.type==='fan_reservation');
    for(const item of fanAssembly){
      const fanBox=placementBox(item.desc,item.placement).bounds;
      const fanOverflow=overflowDistance(bounds,fanBox);
      totalOverflow+=fanOverflow;
      if(fanOverflow>2.0){
        const detail=`radiator_fan_outside:${fanOverflow.toFixed(1)}`;
        reasons.push(detail);
        invalidateComponent(components,'radiator',detail);
      }
      for(const [desc,placement] of placements){
        if(!desc?.actual||!placement||desc.type==='radiator')continue;
        const ratio=overlapRatio(fanBox,placementBox(desc,placement).bounds);
        if(ratio<=0.015)continue;
        totalCollision+=ratio;
        const detail=`radiator_fan_${desc.type}_collision:${ratio.toFixed(3)}`;
        reasons.push(detail);
        invalidateComponent(components,'radiator',detail);
        invalidateComponent(components,desc.type,detail);
      }
    }
  }
  return {
    valid: reasons.length===0,
    reasons,
    overflow:totalOverflow,
    collision:totalCollision,
    components
  };
}

function annotateExpectedSize(candidate, desc) {
  if (!candidate || !desc) return candidate;
  return {
    ...candidate,
    expectedWorldSize: orientedSize(componentLocalSize(desc.type,desc.dims),candidate.orientationClass)
  };
}

function worldCandidateList(items,transformClass,desc=null){
  return (items||[]).map(item=>annotateExpectedSize(canonicalPlacementToWorld(item,transformClass),desc));
}


function projectAssemblyToWorld(items, transformClass) {
  return (items || []).map(item => ({
    ...item,
    placement: item?.placement ? canonicalPlacementToWorld(item.placement, transformClass) : null
  })).filter(item => item.placement);
}

function projectReservationsToWorld(reservations, transformClass) {
  if (!reservations || typeof reservations !== 'object') return reservations || null;
  const out={...reservations};
  for (const key of ['gpu','cpu','motherboardKeepout','psuPlatform','radiator']) {
    const value=reservations[key];
    if (value?.position) out[key]=canonicalPlacementToWorld(value,transformClass);
  }
  if (Array.isArray(reservations.radiatorAssembly)) out.radiatorAssembly=projectAssemblyToWorld(reservations.radiatorAssembly,transformClass);
  if (Array.isArray(reservations.structuralRadiatorAssembly)) out.structuralRadiatorAssembly=projectAssemblyToWorld(reservations.structuralRadiatorAssembly,transformClass);
  return out;
}

function solutionToPlan(solution,inventory){
  const a=solution.arrangement;
  const transform=solution.transformClass;
  const primary={
    motherboard:annotateExpectedSize(canonicalPlacementToWorld(a.mobo,transform),inventory.mobo),
    gpu:annotateExpectedSize(canonicalPlacementToWorld(a.gpu,transform),inventory.gpu),
    psu:a.psu?annotateExpectedSize(canonicalPlacementToWorld(a.psu,transform),inventory.psu):null,
    cooler:a.cooler?annotateExpectedSize(canonicalPlacementToWorld(a.cooler,transform),inventory.cooler):null,
    radiator:a.radiator&&inventory.radiator?annotateExpectedSize(canonicalPlacementToWorld(a.radiator,transform),inventory.radiator):null
  };
  const projectedCoolingAssembly=projectAssemblyToWorld(a.reservations?.radiatorAssembly || [],transform);
  if(primary.radiator){
    primary.radiator.coolingAssembly=projectedCoolingAssembly;
    primary.radiator.autoFanPlan=projectedCoolingAssembly
      .filter(item=>item?.desc?.type==='fan_reservation')
      .map((item,index)=>({
        index,
        position:[...item.placement.position],
        orientationClass:item.placement.orientationClass,
        dims:{...item.desc.dims},
        mount:item.placement.mount
      }));
  }
  const projectedRadiatorCandidates=worldCandidateList(a.radiatorCandidates,transform,inventory.radiator);
  const radiatorCandidates=primary.radiator
    ? [primary.radiator,...projectedRadiatorCandidates.filter(item=>item.id!==primary.radiator.id)]
    : projectedRadiatorCandidates;
  const candidates={
    motherboard:[primary.motherboard],
    gpu:[primary.gpu],
    psu:worldCandidateList(a.psuCandidates,transform,inventory.psu),
    radiator:radiatorCandidates
  };
  // Preserve one useful alternate motherboard/GPU orientation in the chosen topology;
  // the rest of the complexity stays in solver alternatives instead of the UI.
  // Motherboard in-plane orientation is topology-owned. Square ITX/mATX boards must not
  // gain an arbitrary quarter-turn candidate just because their AABB is unchanged.
  const verticalGpuMount=String(primary.gpu?.mount||'').includes('vertical_gpu');
  const altGpuClass=verticalGpuMount ? (Number(primary.gpu.orientationClass)===6?12:6) : (solution.family==='reference'?21:12);
  const altGpu=annotateExpectedSize({...primary.gpu,id:`${primary.gpu.id}_face_alt`,orientationClass:composeOrientationClasses(transform,altGpuClass),priority:60},inventory.gpu);
  candidates.gpu.push(altGpu);

  const canonicalGpuBox=placementBox(inventory.gpu,a.gpu);
  const canonicalCoolerBox=placementBox(inventory.cooler,a.cooler);
  const envelopes={
    gpu:canonicalEnvelopeToWorld({position:canonicalGpuBox.center,size:canonicalGpuBox.size},transform),
    cpu:canonicalEnvelopeToWorld({position:canonicalCoolerBox.center,size:canonicalCoolerBox.size},transform)
  };
  return {primary,candidates,envelopes,reservations:projectReservationsToWorld(a.reservations,transform)};
}

function placementConfidenceFromSolutions(solutions){
  if(!solutions.length)return 0;
  const top=solutions[0],second=solutions[1];
  const gap=second?top.score-second.score:35;
  const gapConfidence=1/(1+Math.exp(-(gap-4)/12));
  const penaltyFactor=Math.max(0.2,1-Math.min(0.8,top.fit.hardPenalty/120));
  return Math.max(0.05,Math.min(0.98,gapConfidence*penaltyFactor));
}

function topologyConfidenceFromSolutions(solutions){
  if(!solutions.length)return 0;
  const top=solutions[0];
  const otherFamily=solutions.find(solution=>solution.family!==top.family);
  if(otherFamily){
    const familyGap=top.score-otherFamily.score;
    const familyGapConfidence=1/(1+Math.exp(-(familyGap-2)/10));
    return Math.max(0.10,Math.min(0.98,familyGapConfidence));
  }

  // When structured state has already reduced the solve to one family, arrangement ties
  // must not masquerade as topology uncertainty. Confidence reflects the authority that
  // established the family, while placement ambiguity is reported separately.
  const source=String(top?._effective?.layoutFamilySource||'');
  const reasons=new Set(top?.priorReasons||[]);
  let base=0.86;
  if(source==='active_mode_explicit' || source==='case_or_structured_declared') base=0.98;
  else if(reasons.has('structured_spatial_family') || reasons.has('active_mode_family')) base=0.98;
  else if(reasons.has('structured_family_evidence')) base=0.96;
  else if(reasons.has('has_spine') || reasons.has('fixed_steps') || reasons.has('capability_pool_spine_signature')) base=0.93;
  return Math.max(0.10,Math.min(0.98,base));
}

function rankCandidateRecord(solution, index) {
  const inventory=solution._inventory;
  const effective=solution._effective;
  const capabilityProfile=solution._capabilityProfile;
  const projected=solutionToPlan(solution,inventory);
  const hard=hardArrangementValidation(solution,inventory,effective,capabilityProfile);
  return {
    rankId:`rank_${index}_${solution._branch?.id || 'cfg'}_${solution.id}_${solution.arrangement?.psu?.id || 'no_psu'}`,
    topologyId:solution.id,
    family:solution.family,
    variant:solution.variant,
    transformClass:solution.transformClass,
    configurationBranchId:solution._branch?.id || null,
    deterministicScore:solution.score,
    hardPenalty:solution.fit.hardPenalty,
    hardValid:hard.valid,
    hardDiagnostics:hard,
    componentValidity:hard.components || {},
    reasons:[...solution.priorReasons,...solution.fit.reasons,...(solution._branch?.reasons||[])],
    spineX:solution.arrangement.spineX,
    primary:projected.primary,
    candidates:projected.candidates,
    envelopes:projected.envelopes,
    reservations:projected.reservations
  };
}

export function buildConstraintSolutions({caseObj,overlay,effective,components=[]}){
  const capabilityProfile=buildCaseCapabilityProfile(caseObj,overlay);
  const branches=buildConfigurationBranches({caseObj,overlay,effective,capabilityProfile,components});
  const branchPool=branches.length?branches:[{
    id:'cfg_fallback',mobo:largestSupportedMotherboard(caseObj),psu:largestSupportedPsu(caseObj),
    cooling:finite(effective?.limits?.maxAIO,0)>0?'liquid':'air',rad:finite(effective?.limits?.maxAIO,0),
    radFan:'standard',fanThickness:25,limits:{...(effective?.limits||{})},
    psuPolicy:{orientations:String(effective?.layoutFamily||'')==='console'?'horizontal_or_upright':'horizontal'},
    valid:true,priority:100,reasons:['branch_fallback']
  }];

  const allSolutions=[];
  for(const branch of branchPool){
    const branchEffective=branchEffectiveState(effective,branch);
    const inventory=buildInventory(caseObj,branchEffective,components,branch);
    const hypotheses=enumerateTopologyHypotheses(caseObj,overlay,branchEffective,capabilityProfile);
    for(const solution of hypotheses.flatMap(h=>solveHypothesisCandidates(h,inventory,branchEffective,capabilityProfile))){
      solution._inventory=inventory;
      solution._effective=branchEffective;
      solution._branch=branch;
      solution._capabilityProfile=capabilityProfile;
      allSolutions.push(solution);
    }
  }

  allSolutions.sort((a,b)=>b.score-a.score);
  if(!allSolutions.length)return null;
  const validated=allSolutions.map((solution,index)=>({
    solution,index,
    hard:hardArrangementValidation(solution,solution._inventory,solution._effective,capabilityProfile)
  }));
  const bestEntry=validated.find(item=>item.hard.valid) || validated[0];
  const best=bestEntry.solution;
  const bestInventory=best._inventory;
  const bestEffective=best._effective;
  const bestBranch=best._branch;
  const projected=solutionToPlan(best,bestInventory);

  const allValidEntries=validated.filter(item=>item.hard.valid);
  const confidenceSolutions=allValidEntries.length?allValidEntries.map(item=>item.solution):allSolutions;
  const validEntries=allValidEntries.slice(0,18);
  const invalidEntries=validated.filter(item=>!item.hard.valid).slice(0,Math.max(0,24-validEntries.length));
  const rankingEntries=[...validEntries,...invalidEntries].slice(0,24);
  const rankingCandidates=rankingEntries.map((item,i)=>rankCandidateRecord(item.solution,i));

  return {
    schemaVersion:8,
    inventory:bestInventory,
    effective:bestEffective,
    capabilityProfile,
    configurationBranch:bestBranch,
    configurationBranches:branchPool.map(branch=>({
      id:branch.id,mobo:branch.mobo,psu:branch.psu,cooling:branch.cooling,rad:branch.rad,
      radFan:branch.radFan,fanThickness:branch.fanThickness,reserveRad:branch.reserveRad,
      reserveRadFan:branch.reserveRadFan,reserveFanThickness:branch.reserveFanThickness,
      valid:branch.valid,priority:branch.priority,
      structuralLimits:{...(branch.structuralLimits||branch.limits||{})},
      validationLimits:{...(branch.validationLimits||branch.limits||{})},
      psuPolicy:branch.psuPolicy,matchedRules:branch.matchedRules||[],failures:branch.failures||[],failureDetails:branch.failureDetails||[],warnings:branch.warnings||[]
    })),
    hasHardValidSolution:!!bestEntry.hard.valid,
    hardDiagnostics:bestEntry.hard,
    componentValidity:bestEntry.hard.components || {},
    best:{
      id:best.id,family:best.family,variant:best.variant,transformClass:best.transformClass,
      score:best.score,hardPenalty:best.fit.hardPenalty,
      reasons:[...best.priorReasons,...best.fit.reasons,...(bestBranch?.reasons||[])],
      spineX:best.arrangement.spineX,
      configurationBranchId:bestBranch?.id || null
    },
    confidence:topologyConfidenceFromSolutions(confidenceSolutions),
    placementConfidence:placementConfidenceFromSolutions(confidenceSolutions),
    alternatives:allSolutions.slice(1,4).map(s=>({
      id:s.id,family:s.family,variant:s.variant,score:s.score,hardPenalty:s.fit.hardPenalty,
      configurationBranchId:s._branch?.id || null
    })),
    rankingCandidates,
    ...projected
  };
}
